import { type ChildProcessWithoutNullStreams } from "node:child_process";
import { randomUUID } from "node:crypto";
import fs from "node:fs";
import { promises } from "node:fs";
import { createRequire } from "node:module";
import os from "node:os";
import path from "node:path";
import {
  query,
  type AgentDefinition,
  type CanUseTool,
  type McpServerConfig as ClaudeSdkMcpServerConfig,
  type Options,
  type SdkBeta,
  type PermissionMode,
  type PermissionResult,
  type PermissionUpdate,
  type Query,
  type SpawnOptions,
  type SDKMessage,
  type SDKPartialAssistantMessage,
  type SDKResultMessage,
  type SDKSystemMessage,
  type SDKUserMessage,
} from "@anthropic-ai/claude-agent-sdk";
import type { Logger } from "pino";
import {
  mapClaudeCanceledToolCall,
  mapClaudeCompletedToolCall,
  mapClaudeFailedToolCall,
  mapClaudeRunningToolCall,
} from "./claude/tool-call-mapper.js";
import {
  mapTaskNotificationSystemRecordToToolCall,
  mapTaskNotificationUserContentToToolCall,
} from "./claude/task-notification-tool-call.js";
import {
  getClaudeModelBetas,
  getClaudeModels,
  normalizeClaudeRuntimeModelId,
} from "./claude/claude-models.js";
import { getClaudeUsageProbe } from "./claude/usage-probe.js";
import { buildClaudeFeatures } from "./claude-feature-definitions.js";
import { parsePartialJsonObject } from "./claude/partial-json.js";
import { ClaudeSidechainTracker } from "./claude/sidechain-tracker.js";
import {
  formatDiagnosticStatus,
  formatProviderDiagnostic,
  formatProviderDiagnosticError,
  toDiagnosticErrorMessage,
} from "./diagnostic-utils.js";
import { renderPromptAttachmentAsText } from "../prompt-attachments.js";
import { composeAppostleSystemPrompt } from "../appostle-system-prompt.js";
import {
  extractPlanNameFromFrontmatter,
  resolvePlanFilename,
  slugifyPlanName,
} from "../plan-filename.js";

import type {
  AgentPermissionAction,
  AgentCapabilityFlags,
  AgentClient,
  AgentFeature,
  AgentLaunchContext,
  AgentMetadata,
  AgentMode,
  AgentModelDefinition,
  AgentPermissionRequest,
  AgentPermissionRequestKind,
  AgentPermissionResponse,
  AgentPermissionUpdate,
  AgentPersistenceHandle,
  AgentPromptInput,
  AgentRateLimit,
  AgentRunOptions,
  AgentRunResult,
  AgentSession,
  AgentSessionConfig,
  AgentSlashCommand,
  AgentStreamEvent,
  AgentTimelineItem,
  AgentUsage,
  AgentRuntimeInfo,
  ListModelsOptions,
  ListPersistedAgentsOptions,
  McpServerConfig,
  PersistedAgentDescriptor,
  RateLimitType,
} from "../agent-sdk-types.js";
import { applyProviderEnv, type ProviderRuntimeSettings } from "../provider-launch-config.js";
import { findExecutable, isCommandAvailable } from "../../../utils/executable.js";
import { execCommand, spawnProcess } from "../../../utils/spawn.js";
import { getSystemReminderGuidance } from "../orchestrator-instructions.js";

const fsPromises = promises;

// Mirrors the resolution the SDK does internally so we can verify install
// state up-front in `isAvailable()` / `getDiagnostic()`. SDK ≥ 0.3.x ships
// the actual Claude CLI as a native binary inside a per-platform sibling
// package (@anthropic-ai/claude-agent-sdk-${platform}-${arch}); on linux
// the musl variant is tried first. See sdk.mjs `N7()`.
function resolveClaudeNativeBinary(): string | null {
  const platform = process.platform;
  const arch = process.arch;
  const ext = platform === "win32" ? ".exe" : "";
  const candidates =
    platform === "linux"
      ? [
          `@anthropic-ai/claude-agent-sdk-linux-${arch}-musl/claude${ext}`,
          `@anthropic-ai/claude-agent-sdk-linux-${arch}/claude${ext}`,
        ]
      : [`@anthropic-ai/claude-agent-sdk-${platform}-${arch}/claude${ext}`];
  const req = createRequire(import.meta.url);
  for (const id of candidates) {
    try {
      return req.resolve(id);
    } catch {
      // try next
    }
  }
  return null;
}
const CLAUDE_SETTING_SOURCES: NonNullable<Options["settingSources"]> = ["user", "project"];

type TurnState = "idle" | "foreground" | "autonomous";

type EventIdentifiers = {
  taskId: string | null;
  parentMessageId: string | null;
  messageId: string | null;
};

type AutonomousTurnState = {
  id: string;
};

type AsyncMessageInput<T> = {
  push: (item: T) => void;
  end: () => void;
  iterable: AsyncIterable<T>;
};

const CLAUDE_CAPABILITIES: AgentCapabilityFlags = {
  supportsStreaming: true,
  supportsSessionPersistence: true,
  supportsDynamicModes: true,
  supportsMcpServers: true,
  supportsReasoningStream: true,
  supportsToolInvocations: true,
};

const DEFAULT_MODES: AgentMode[] = [
  {
    id: "default",
    label: "Always Ask",
    description: "Prompts for permission the first time a tool is used",
  },
  {
    id: "acceptEdits",
    label: "Accept File Edits",
    description: "Automatically approves edit-focused tools without prompting",
  },
  {
    id: "plan",
    label: "Plan Mode",
    description: "Analyze the codebase without executing tools or edits",
  },
  {
    id: "bypassPermissions",
    label: "Bypass",
    description: "Skip all permission prompts (use with caution)",
  },
];

const VALID_CLAUDE_MODES = new Set(DEFAULT_MODES.map((mode) => mode.id));

// Tools that the "acceptEdits" permission mode auto-approves. This intentionally
// extends beyond the upstream Claude Code CLI (which only auto-accepts file edits)
// by also auto-accepting Bash, so a user who picks "Accept File Edits" in the
// appostle UI gets a fluent edit-and-iterate loop without per-shell-command prompts.
const CLAUDE_ACCEPT_EDITS_AUTO_ALLOWED = new Set([
  "Edit",
  "Write",
  "MultiEdit",
  "NotebookEdit",
  "Bash",
]);

const REWIND_COMMAND_NAME = "rewind";
const REWIND_COMMAND: AgentSlashCommand = {
  name: REWIND_COMMAND_NAME,
  description: "Rewind tracked files to a previous user message",
  argumentHint: "[user_message_uuid]",
};
const INTERRUPT_TOOL_USE_PLACEHOLDER = "[Request interrupted by user for tool use]";
const INTERRUPT_PLACEHOLDER_PATTERN = /^\[Request interrupted by user(?:[^\]]*)\]$/;
const NO_RESPONSE_REQUESTED_PLACEHOLDER = "No response requested.";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type SlashCommandInvocation = {
  commandName: string;
  args?: string;
  rawInput: string;
};

// Orchestrator instructions moved to shared module.
type ClaudeAgentConfig = AgentSessionConfig & { provider: "claude" };

export type ClaudeContentChunk = { type: string; [key: string]: any };

type ClaudeOptions = Options;

type ClaudeAgentClientOptions = {
  defaults?: { agents?: Record<string, AgentDefinition> };
  logger: Logger;
  runtimeSettings?: ProviderRuntimeSettings;
  queryFactory?: typeof query;
};

type ClaudeAgentSessionOptions = {
  defaults?: { agents?: Record<string, AgentDefinition> };
  runtimeSettings?: ProviderRuntimeSettings;
  handle?: AgentPersistenceHandle;
  launchEnv?: Record<string, string>;
  chromeEnabled?: boolean;
  playwrightEnabled?: boolean;
  logger: Logger;
  queryFactory?: typeof query;
};

type ClaudeThinkingEffort = "low" | "medium" | "high" | "xhigh" | "max";

function resolveClaudeSpawnCommand(
  spawnOptions: SpawnOptions,
  runtimeSettings?: ProviderRuntimeSettings,
): { command: string; args: string[] } {
  const commandConfig = runtimeSettings?.command;
  if (!commandConfig || commandConfig.mode === "default") {
    return {
      command: spawnOptions.command,
      args: [...spawnOptions.args],
    };
  }

  if (commandConfig.mode === "append") {
    return {
      command: spawnOptions.command,
      args: [...spawnOptions.args, ...(commandConfig.args ?? [])],
    };
  }

  return {
    command: commandConfig.argv[0]!,
    args: [...commandConfig.argv.slice(1), ...spawnOptions.args],
  };
}

function applyRuntimeSettingsToClaudeOptions(
  options: ClaudeOptions,
  runtimeSettings?: ProviderRuntimeSettings,
  launchEnv?: Record<string, string>,
): ClaudeOptions {
  return {
    ...options,
    spawnClaudeCodeProcess: (spawnOptions) => {
      const resolved = resolveClaudeSpawnCommand(spawnOptions, runtimeSettings);
      // When the SDK passes a default JS runtime ("node"/"bun"), replace it with
      // process.execPath — the actual node binary running the daemon. This avoids
      // PATH lookup failures in the managed runtime bundle.
      // When the SDK passes a native binary path (from pathToClaudeCodeExecutable)
      // or the user overrides the command via runtime settings, use that directly.
      const isDefaultRuntime = resolved.command === "node" || resolved.command === "bun";
      const command = isDefaultRuntime ? process.execPath : resolved.command;
      const child = spawnProcess(command, resolved.args, {
        cwd: spawnOptions.cwd,
        env: {
          ...applyProviderEnv(spawnOptions.env, runtimeSettings),
          ...(launchEnv ?? {}),
        },
        signal: spawnOptions.signal,
        stdio: ["pipe", "pipe", "pipe"],
        // Bypass cmd.exe on Windows: the SDK passes --mcp-config with inline JSON
        // containing double quotes, which cmd.exe mangles (strips quotes, breaks parsing).
        // The command is always a resolved binary path, so shell routing is unnecessary.
        shell: false,
      });
      if (typeof options.stderr === "function") {
        child.stderr?.on("data", (chunk: Buffer | string) => {
          options.stderr?.(chunk.toString());
        });
      }
      return child as ChildProcessWithoutNullStreams;
    },
  };
}

function isClaudeThinkingEffort(value: string | null | undefined): value is ClaudeThinkingEffort {
  return (
    value === "low" ||
    value === "medium" ||
    value === "high" ||
    value === "xhigh" ||
    value === "max"
  );
}

function sanitizeClaudeProjectPath(cwd: string): string {
  // Must match Claude Code's own project-dir naming under ~/.claude/projects/,
  // which replaces EVERY non-alphanumeric character (including spaces) with "-".
  // A narrower class (e.g. only \/._:) leaves spaces intact and diverges for
  // cwds like "/Users/x/Projects_Oh Lord/Final Nutrition", so resolveHistoryPath
  // points at a directory that doesn't exist and the transcript loads empty.
  return cwd.replace(/[^a-zA-Z0-9]/g, "-");
}

type ClaudeOptionsLogSummary = {
  cwd: string | null;
  permissionMode: string | null;
  model: string | null;
  includePartialMessages: boolean;
  settingSources: string[];
  enableFileCheckpointing: boolean;
  hasResume: boolean;
  maxThinkingTokens: number | null;
  hasEnv: boolean;
  envKeyCount: number;
  hasMcpServers: boolean;
  mcpServerNames: string[];
  systemPromptMode: "none" | "string" | "preset" | "custom";
  systemPromptPreset: string | null;
  hasCanUseTool: boolean;
  hasSpawnOverride: boolean;
  hasStderrHandler: boolean;
  pathToClaudeCodeExecutable: string | null;
};

const MAX_RECENT_STDERR_CHARS = 4000;
const STDERR_FLUSH_WAIT_MS = 150;
const STDERR_FLUSH_POLL_INTERVAL_MS = 10;

function summarizeClaudeOptionsForLog(options: ClaudeOptions): ClaudeOptionsLogSummary {
  const systemPromptRaw = options.systemPrompt;
  const systemPromptSummary = (() => {
    if (!systemPromptRaw) {
      return { mode: "none" as const, preset: null };
    }
    if (typeof systemPromptRaw === "string") {
      return { mode: "string" as const, preset: null };
    }
    const prompt = systemPromptRaw as Record<string, unknown>;
    const promptType = typeof prompt.type === "string" ? prompt.type : "custom";
    return {
      mode: promptType === "preset" ? ("preset" as const) : ("custom" as const),
      preset: typeof prompt.preset === "string" && prompt.preset.length > 0 ? prompt.preset : null,
    };
  })();
  const mcpServerNames = options.mcpServers ? Object.keys(options.mcpServers).sort() : [];

  return {
    cwd: typeof options.cwd === "string" ? options.cwd : null,
    permissionMode: typeof options.permissionMode === "string" ? options.permissionMode : null,
    model: typeof options.model === "string" ? options.model : null,
    includePartialMessages: options.includePartialMessages === true,
    settingSources: Array.isArray(options.settingSources) ? options.settingSources : [],
    enableFileCheckpointing: options.enableFileCheckpointing === true,
    hasResume: typeof options.resume === "string" && options.resume.length > 0,
    maxThinkingTokens:
      typeof options.maxThinkingTokens === "number" ? options.maxThinkingTokens : null,
    hasEnv: !!options.env,
    envKeyCount: Object.keys(options.env ?? {}).length,
    hasMcpServers: mcpServerNames.length > 0,
    mcpServerNames,
    systemPromptMode: systemPromptSummary.mode,
    systemPromptPreset: systemPromptSummary.preset,
    hasCanUseTool: typeof options.canUseTool === "function",
    hasSpawnOverride: typeof options.spawnClaudeCodeProcess === "function",
    hasStderrHandler: typeof options.stderr === "function",
    pathToClaudeCodeExecutable:
      typeof options.pathToClaudeCodeExecutable === "string"
        ? options.pathToClaudeCodeExecutable
        : null,
  };
}

function isToolResultTextBlock(value: unknown): value is { type: "text"; text: string } {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { type?: unknown }).type === "text" &&
    typeof (value as { text?: unknown }).text === "string"
  );
}

/**
 * Scan raw tool-result content for Anthropic-style image content blocks and
 * return just those blocks. Used to short-circuit `buildToolOutput` so any tool
 * that returns an inline image (the built-in Read tool viewing a .png, MCP
 * screenshot tools) surfaces as an image detail instead of being coerced into a
 * base64 text wall by a name-based structured handler (e.g. the `file_read`
 * branch). Downstream `extractImageToolDetail` understands this `{ type: "image",
 * source: { data, media_type } }` shape.
 */
function extractImageContentBlocks(content: unknown): unknown[] | undefined {
  if (!Array.isArray(content)) {
    return undefined;
  }
  const images = content.filter(
    (block) =>
      !!block && typeof block === "object" && (block as { type?: unknown }).type === "image",
  );
  return images.length > 0 ? images : undefined;
}

function normalizeForDeterministicString(value: unknown, seen: WeakSet<object>): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (typeof value === "function") {
    return "[function]";
  }
  if (typeof value === "symbol") {
    return value.toString();
  }
  if (typeof value === "undefined") {
    return "[undefined]";
  }
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeForDeterministicString(entry, seen));
  }
  if (typeof value === "object") {
    const objectValue = value as object;
    if (seen.has(objectValue)) {
      return "[circular]";
    }
    seen.add(objectValue);
    const record = value as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      normalized[key] = normalizeForDeterministicString(record[key], seen);
    }
    seen.delete(objectValue);
    return normalized;
  }
  return String(value);
}

function deterministicStringify(value: unknown): string {
  if (typeof value === "undefined") {
    return "";
  }
  try {
    const normalized = normalizeForDeterministicString(value, new WeakSet<object>());
    if (typeof normalized === "string") {
      return normalized;
    }
    return JSON.stringify(normalized);
  } catch {
    try {
      return String(value);
    } catch {
      return "[unserializable]";
    }
  }
}

function coerceToolResultContentToString(content: unknown): string {
  if (typeof content === "string") {
    return content;
  }
  if (Array.isArray(content) && content.every((block) => isToolResultTextBlock(block))) {
    return content.map((block) => block.text).join("");
  }
  return deterministicStringify(content);
}

function normalizeClaudeTranscriptText(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isClaudeInterruptPlaceholderText(value: unknown): boolean {
  const normalized = normalizeClaudeTranscriptText(value);
  return normalized !== null && INTERRUPT_PLACEHOLDER_PATTERN.test(normalized);
}

function isClaudeNoResponsePlaceholderText(value: unknown): boolean {
  return normalizeClaudeTranscriptText(value) === NO_RESPONSE_REQUESTED_PLACEHOLDER;
}

const LOCAL_COMMAND_STDOUT_PATTERN =
  /^\s*<local-command-stdout>[\s\S]*<\/local-command-stdout>\s*$/;

function isClaudeLocalCommandStdout(value: unknown): boolean {
  const normalized = normalizeClaudeTranscriptText(value);
  return normalized !== null && LOCAL_COMMAND_STDOUT_PATTERN.test(normalized);
}

function isClaudeTranscriptNoiseText(value: unknown): boolean {
  return (
    isClaudeInterruptPlaceholderText(value) ||
    isClaudeNoResponsePlaceholderText(value) ||
    isClaudeLocalCommandStdout(value)
  );
}

function collectClaudeTextContentParts(content: unknown): string[] {
  if (typeof content === "string") {
    const normalized = normalizeClaudeTranscriptText(content);
    return normalized ? [normalized] : [];
  }

  if (!Array.isArray(content)) {
    return [];
  }

  const parts: string[] = [];
  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const text = normalizeClaudeTranscriptText((block as { text?: unknown }).text);
    if (text) {
      parts.push(text);
      continue;
    }
    const input = normalizeClaudeTranscriptText((block as { input?: unknown }).input);
    if (input) {
      parts.push(input);
    }
  }

  return parts;
}

export function isClaudeTranscriptNoiseContent(content: unknown): boolean {
  const parts = collectClaudeTextContentParts(content);
  return parts.length > 0 && parts.every((part) => isClaudeTranscriptNoiseText(part));
}

export function extractUserMessageText(content: unknown): string | null {
  if (typeof content === "string") {
    const normalized = content.trim();
    if (!normalized || isClaudeTranscriptNoiseText(normalized)) {
      return null;
    }
    return normalized;
  }

  if (!Array.isArray(content)) {
    return null;
  }

  const parts: string[] = [];
  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const text = typeof block.text === "string" ? block.text : undefined;
    if (text && text.trim()) {
      const trimmed = text.trim();
      if (!isClaudeTranscriptNoiseText(trimmed)) {
        parts.push(trimmed);
      }
      continue;
    }
    const input = typeof block.input === "string" ? block.input : undefined;
    if (input && input.trim()) {
      const trimmed = input.trim();
      if (!isClaudeTranscriptNoiseText(trimmed)) {
        parts.push(trimmed);
      }
    }
  }

  if (parts.length === 0) {
    return null;
  }

  const combined = parts.join("\n\n").trim();
  return combined.length > 0 ? combined : null;
}

type PendingPermission = {
  request: AgentPermissionRequest;
  resolve: (result: PermissionResult) => void;
  reject: (error: Error) => void;
  cleanup?: () => void;
};

type ToolUseClassification = "generic" | "command" | "file_change";
type ToolUseCacheEntry = {
  id: string;
  name: string;
  server: string;
  classification: ToolUseClassification;
  started: boolean;
  commandText?: string;
  files?: { path: string; kind: string }[];
  input?: AgentMetadata | null;
};
function isMetadata(value: unknown): value is AgentMetadata {
  return typeof value === "object" && value !== null;
}

function readTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function isMcpServerConfig(value: unknown): value is McpServerConfig {
  if (!isMetadata(value)) {
    return false;
  }
  const type = value.type;
  if (type === "stdio") {
    return typeof value.command === "string";
  }
  if (type === "http" || type === "sse") {
    return typeof value.url === "string";
  }
  return false;
}

function isMcpServersRecord(value: unknown): value is Record<string, McpServerConfig> {
  if (!isMetadata(value)) {
    return false;
  }
  for (const config of Object.values(value)) {
    if (!isMcpServerConfig(config)) {
      return false;
    }
  }
  return true;
}

function isPermissionMode(value: string | undefined): value is PermissionMode {
  return typeof value === "string" && VALID_CLAUDE_MODES.has(value);
}

function coerceSessionMetadata(metadata: AgentMetadata | undefined): Partial<AgentSessionConfig> {
  if (!isMetadata(metadata)) {
    return {};
  }

  const result: Partial<AgentSessionConfig> = {};
  if (metadata.provider === "claude" || metadata.provider === "codex") {
    result.provider = metadata.provider;
  }
  if (typeof metadata.cwd === "string") {
    result.cwd = metadata.cwd;
  }
  if (typeof metadata.modeId === "string") {
    result.modeId = metadata.modeId;
  }
  if (typeof metadata.model === "string") {
    result.model = metadata.model;
  }
  if (typeof metadata.title === "string" || metadata.title === null) {
    result.title = metadata.title;
  }
  if (typeof metadata.approvalPolicy === "string") {
    result.approvalPolicy = metadata.approvalPolicy;
  }
  if (typeof metadata.sandboxMode === "string") {
    result.sandboxMode = metadata.sandboxMode;
  }
  if (typeof metadata.networkAccess === "boolean") {
    result.networkAccess = metadata.networkAccess;
  }
  if (typeof metadata.webSearch === "boolean") {
    result.webSearch = metadata.webSearch;
  }
  if (isMetadata(metadata.extra)) {
    const extra: AgentSessionConfig["extra"] = {};
    if (isMetadata(metadata.extra.codex)) {
      extra.codex = metadata.extra.codex;
    }
    if (isClaudeExtra(metadata.extra.claude)) {
      extra.claude = metadata.extra.claude;
    }
    if (extra.codex || extra.claude) {
      result.extra = extra;
    }
  }
  if (typeof metadata.systemPrompt === "string") {
    result.systemPrompt = metadata.systemPrompt;
  }
  if (isMcpServersRecord(metadata.mcpServers)) {
    result.mcpServers = metadata.mcpServers;
  }

  return result;
}

function toClaudeSdkMcpConfig(config: McpServerConfig): ClaudeSdkMcpServerConfig {
  switch (config.type) {
    case "stdio":
      return {
        type: "stdio",
        command: config.command,
        args: config.args,
        env: config.env,
      };
    case "http":
      return {
        type: "http",
        url: config.url,
        headers: config.headers,
        ...(config.alwaysLoad !== undefined ? { alwaysLoad: config.alwaysLoad } : {}),
      };
    case "sse":
      return {
        type: "sse",
        url: config.url,
        headers: config.headers,
      };
    case "sdk":
      // In-process server. The Claude SDK accepts the live instance directly;
      // we just unwrap our wrapper and pass it through.
      return config.config;
  }
}

// Cache for ~/.claude.json MCP servers. The file rarely changes during a
// daemon lifetime; re-reading on every agent spawn is wasteful.
let homeMcpServersCache: { mtimeMs: number; servers: Record<string, McpServerConfig> } | null =
  null;

/**
 * Load MCP servers from ~/.claude.json so appostle-spawned claude agents pick up
 * the same MCP tools the terminal `claude` CLI has configured. Returns `null`
 * on any error (missing file, parse failure, no mcpServers entry). Results are
 * cached per-mtime so the file is only re-parsed when it changes.
 */
function loadHomeMcpServers(logger?: Logger): Record<string, McpServerConfig> | null {
  try {
    const home = process.env.HOME ?? os.homedir();
    const filePath = path.join(home, ".claude.json");
    const stat = fs.statSync(filePath);
    if (homeMcpServersCache && homeMcpServersCache.mtimeMs === stat.mtimeMs) {
      return homeMcpServersCache.servers;
    }
    const raw = fs.readFileSync(filePath, "utf8");
    const parsed: unknown = JSON.parse(raw);
    if (!isMetadata(parsed)) return null;
    const rawServers = parsed.mcpServers;
    if (!isMetadata(rawServers)) return null;
    const servers: Record<string, McpServerConfig> = {};
    for (const [name, value] of Object.entries(rawServers)) {
      if (!isMetadata(value)) continue;
      // ~/.claude.json entries omit `type` for stdio servers; normalize.
      const withType = value.type ? value : { ...value, type: "stdio" };
      if (isMcpServerConfig(withType)) {
        servers[name] = withType;
      }
    }
    if (Object.keys(servers).length === 0) return null;
    homeMcpServersCache = { mtimeMs: stat.mtimeMs, servers };
    return servers;
  } catch (err) {
    logger?.debug({ err }, "failed to load ~/.claude.json mcp servers");
    return null;
  }
}

function isClaudeContentChunk(value: unknown): value is ClaudeContentChunk {
  return isMetadata(value) && typeof value.type === "string";
}

function isClaudeExtra(value: unknown): value is Partial<ClaudeOptions> {
  return isMetadata(value);
}

function isPermissionUpdate(value: AgentPermissionUpdate): value is PermissionUpdate {
  if (!isMetadata(value)) {
    return false;
  }
  const type = value.type;
  if (type !== "addRules" && type !== "replaceRules" && type !== "removeRules") {
    return false;
  }
  const rules = value.rules;
  const behavior = value.behavior;
  const destination = value.destination;
  return Array.isArray(rules) && typeof behavior === "string" && typeof destination === "string";
}

function resolvePermissionKind(
  toolName: string,
  input: Record<string, unknown>,
): AgentPermissionRequestKind {
  if (toolName === "ExitPlanMode") return "plan";
  if (toolName === "AskUserQuestion" && Array.isArray(input.questions)) {
    return "question";
  }
  return "tool";
}

function getClaudeModeLabel(modeId: PermissionMode): string {
  return DEFAULT_MODES.find((mode) => mode.id === modeId)?.label ?? modeId;
}

function buildClaudePlanPermissionActions(
  resumeMode: PermissionMode | null,
): AgentPermissionAction[] {
  const actions: AgentPermissionAction[] = [
    {
      id: "reject",
      label: "Reject",
      behavior: "deny",
      variant: "danger",
      intent: "dismiss",
    },
    {
      id: "implement",
      label: "Implement",
      behavior: "allow",
      variant: "primary",
      intent: "implement",
    },
  ];

  if (resumeMode === "bypassPermissions") {
    actions.push({
      id: "implement_resume",
      label: `Implement with ${getClaudeModeLabel(resumeMode)}`,
      behavior: "allow",
      variant: "secondary",
      intent: "implement_resume",
    });
  }

  return actions;
}

type TimelineFragment = {
  kind: "assistant" | "reasoning";
  text: string;
};

type TimelineMessageState = {
  id: string;
  assistantText: string;
  reasoningText: string;
  emittedAssistantLength: number;
  emittedReasoningLength: number;
  stopped: boolean;
};

class TimelineAssembler {
  private readonly messages = new Map<string, TimelineMessageState>();
  private readonly finalizedMessageIds = new Set<string>();
  private readonly activeMessageByRun = new Map<string, string>();
  private syntheticMessageCounter = 0;

  consume(input: {
    message: SDKMessage;
    runId: string | null;
    messageIdHint?: string | null;
  }): AgentTimelineItem[] {
    if (input.message.type === "assistant") {
      return this.consumeAssistantMessage(input.message, input.runId, input.messageIdHint ?? null);
    }
    if (input.message.type === "stream_event") {
      return this.consumeStreamEvent(input.message, input.runId, input.messageIdHint ?? null);
    }
    return [];
  }

  private consumeAssistantMessage(
    message: SDKMessage & { type: "assistant" },
    runId: string | null,
    messageIdHint: string | null,
  ): AgentTimelineItem[] {
    const messageId =
      this.readMessageIdFromAssistantMessage(message) ??
      messageIdHint ??
      this.resolveMessageId({ runId, createIfMissing: true, messageId: null });
    if (!messageId) {
      return [];
    }
    if (this.finalizedMessageIds.has(messageId)) {
      return [];
    }
    const state = this.ensureMessageState(messageId, runId);
    const fragments = this.extractFragments(message.message?.content);
    return this.applyAbsoluteFragments(state, fragments);
  }

  private consumeStreamEvent(
    message: SDKMessage & { type: "stream_event" },
    runId: string | null,
    messageIdHint: string | null,
  ): AgentTimelineItem[] {
    const event = message.event as unknown as Record<string, unknown>;
    const eventType = readTrimmedString(event.type);
    const streamEventMessageId = this.readMessageIdFromStreamEvent(event) ?? messageIdHint;

    if (eventType === "message_start") {
      const messageId = this.resolveMessageId({
        runId,
        createIfMissing: true,
        messageId: streamEventMessageId,
      });
      if (!messageId) {
        return [];
      }
      this.ensureMessageState(messageId, runId);
      return [];
    }

    if (eventType === "message_stop") {
      const messageId = this.resolveMessageId({
        runId,
        createIfMissing: false,
        messageId: streamEventMessageId,
      });
      if (!messageId) {
        return [];
      }
      return this.finalizeMessage(messageId, runId);
    }

    if (eventType === "content_block_start") {
      return this.consumeDeltaContent(event.content_block, runId, streamEventMessageId);
    }

    if (eventType === "content_block_delta") {
      return this.consumeDeltaContent(event.delta, runId, streamEventMessageId);
    }

    return [];
  }

  private consumeDeltaContent(
    content: unknown,
    runId: string | null,
    messageIdHint: string | null,
  ): AgentTimelineItem[] {
    const fragments = this.extractFragments(content);
    if (fragments.length === 0) {
      return [];
    }
    const messageId = this.resolveMessageId({
      runId,
      createIfMissing: true,
      messageId: messageIdHint,
    });
    if (!messageId) {
      return [];
    }
    const state = this.ensureMessageState(messageId, runId);
    return this.appendFragments(state, fragments);
  }

  private appendFragments(
    state: TimelineMessageState,
    fragments: TimelineFragment[],
  ): AgentTimelineItem[] {
    for (const fragment of fragments) {
      if (fragment.kind === "assistant") {
        state.assistantText += fragment.text;
      } else {
        state.reasoningText += fragment.text;
      }
    }
    return this.emitNewContent(state);
  }

  private applyAbsoluteFragments(
    state: TimelineMessageState,
    fragments: TimelineFragment[],
  ): AgentTimelineItem[] {
    const assistantText = fragments
      .filter((fragment) => fragment.kind === "assistant")
      .map((fragment) => fragment.text)
      .join("");
    const reasoningText = fragments
      .filter((fragment) => fragment.kind === "reasoning")
      .map((fragment) => fragment.text)
      .join("");

    if (assistantText.length > 0) {
      if (!assistantText.startsWith(state.assistantText)) {
        state.emittedAssistantLength = 0;
      }
      state.assistantText = assistantText;
    }
    if (reasoningText.length > 0) {
      if (!reasoningText.startsWith(state.reasoningText)) {
        state.emittedReasoningLength = 0;
      }
      state.reasoningText = reasoningText;
    }
    return this.emitNewContent(state);
  }

  private finalizeMessage(messageId: string, runId: string | null): AgentTimelineItem[] {
    const state = this.messages.get(messageId);
    if (!state) {
      return [];
    }
    state.stopped = true;
    const items = this.emitNewContent(state);
    if (runId && this.activeMessageByRun.get(runId) === messageId) {
      this.activeMessageByRun.delete(runId);
    }
    this.finalizedMessageIds.add(messageId);
    this.messages.delete(messageId);
    return items;
  }

  private emitNewContent(state: TimelineMessageState): AgentTimelineItem[] {
    const items: AgentTimelineItem[] = [];
    const nextAssistantText = state.assistantText.slice(state.emittedAssistantLength);
    if (
      nextAssistantText.length > 0 &&
      nextAssistantText !== INTERRUPT_TOOL_USE_PLACEHOLDER &&
      !isClaudeTranscriptNoiseText(nextAssistantText)
    ) {
      state.emittedAssistantLength = state.assistantText.length;
      items.push({ type: "assistant_message", text: nextAssistantText });
    }

    const nextReasoningText = state.reasoningText.slice(state.emittedReasoningLength);
    if (nextReasoningText.length > 0) {
      state.emittedReasoningLength = state.reasoningText.length;
      items.push({ type: "reasoning", text: nextReasoningText });
    }
    return items;
  }

  private ensureMessageState(messageId: string, runId: string | null): TimelineMessageState {
    const existing = this.messages.get(messageId);
    if (existing) {
      existing.stopped = false;
      if (runId) {
        this.activeMessageByRun.set(runId, messageId);
      }
      return existing;
    }
    const created: TimelineMessageState = {
      id: messageId,
      assistantText: "",
      reasoningText: "",
      emittedAssistantLength: 0,
      emittedReasoningLength: 0,
      stopped: false,
    };
    this.messages.set(messageId, created);
    if (runId) {
      this.activeMessageByRun.set(runId, messageId);
    }
    return created;
  }

  private resolveMessageId(input: {
    runId: string | null;
    createIfMissing: boolean;
    messageId: string | null;
  }): string | null {
    if (input.messageId) {
      return input.messageId;
    }
    if (input.runId) {
      const active = this.activeMessageByRun.get(input.runId);
      if (active) {
        return active;
      }
    }
    if (!input.createIfMissing) {
      return null;
    }
    const synthetic = `synthetic-message-${++this.syntheticMessageCounter}`;
    if (input.runId) {
      this.activeMessageByRun.set(input.runId, synthetic);
    }
    return synthetic;
  }

  private extractFragments(content: unknown): TimelineFragment[] {
    if (typeof content === "string") {
      if (content.length === 0) {
        return [];
      }
      return [{ kind: "assistant", text: content }];
    }
    const blocks = Array.isArray(content) ? content : [content];
    const fragments: TimelineFragment[] = [];
    for (const rawBlock of blocks) {
      if (!isClaudeContentChunk(rawBlock)) {
        continue;
      }
      if (
        (rawBlock.type === "text" || rawBlock.type === "text_delta") &&
        typeof rawBlock.text === "string" &&
        rawBlock.text.length > 0
      ) {
        fragments.push({ kind: "assistant", text: rawBlock.text });
      }
      if (
        (rawBlock.type === "thinking" || rawBlock.type === "thinking_delta") &&
        typeof rawBlock.thinking === "string" &&
        rawBlock.thinking.length > 0
      ) {
        fragments.push({ kind: "reasoning", text: rawBlock.thinking });
      }
    }
    return fragments;
  }

  private readMessageIdFromAssistantMessage(
    message: SDKMessage & { type: "assistant" },
  ): string | null {
    const candidate = message as unknown as {
      message_id?: unknown;
      message?: { id?: unknown } | null;
    };
    return (
      readTrimmedString(candidate.message_id) ?? readTrimmedString(candidate.message?.id) ?? null
    );
  }

  private readMessageIdFromStreamEvent(event: Record<string, unknown>): string | null {
    const message = event.message as { id?: unknown } | undefined;
    return readTrimmedString(event.message_id) ?? readTrimmedString(message?.id) ?? null;
  }
}

function isSyntheticUserEntry(entry: unknown): boolean {
  if (!entry || typeof entry !== "object") {
    return false;
  }
  const candidate = entry as { isSynthetic?: unknown; isMeta?: unknown };
  return candidate.isSynthetic === true || candidate.isMeta === true;
}

export function readEventIdentifiers(message: SDKMessage): EventIdentifiers {
  const root = message as unknown as Record<string, unknown>;
  const messageType = readTrimmedString(root.type);
  const streamEvent = root.event as Record<string, unknown> | undefined;
  const streamEventMessage = streamEvent?.message as Record<string, unknown> | undefined;
  const messageContainer = root.message as Record<string, unknown> | undefined;

  return {
    taskId:
      readTrimmedString(root.task_id) ??
      readTrimmedString(streamEvent?.task_id) ??
      readTrimmedString(streamEventMessage?.task_id) ??
      readTrimmedString(messageContainer?.task_id) ??
      null,
    parentMessageId:
      readTrimmedString(root.parent_message_id) ??
      readTrimmedString(streamEvent?.parent_message_id) ??
      readTrimmedString(streamEventMessage?.parent_message_id) ??
      readTrimmedString(messageContainer?.parent_message_id) ??
      null,
    messageId:
      readTrimmedString(root.message_id) ??
      readTrimmedString(streamEvent?.message_id) ??
      readTrimmedString(streamEventMessage?.id) ??
      readTrimmedString(streamEventMessage?.message_id) ??
      readTrimmedString(messageContainer?.id) ??
      readTrimmedString(messageContainer?.message_id) ??
      (messageType === "user" ? readTrimmedString(root.uuid) : null) ??
      null,
  };
}

const claudeDebug = process.env.APPOSTLE_CLAUDE_DEBUG === "1";

export class ClaudeAgentClient implements AgentClient {
  readonly provider: "claude" = "claude";
  readonly capabilities = CLAUDE_CAPABILITIES;

  private readonly defaults?: { agents?: Record<string, AgentDefinition> };
  private readonly logger: Logger;
  private readonly runtimeSettings?: ProviderRuntimeSettings;
  private readonly queryFactory: typeof query;

  constructor(options: ClaudeAgentClientOptions) {
    this.defaults = options.defaults;
    this.logger = options.logger.child({ module: "agent", provider: "claude" });
    this.runtimeSettings = options.runtimeSettings;
    this.queryFactory = options.queryFactory ?? query;
  }

  async createSession(
    config: AgentSessionConfig,
    launchContext?: AgentLaunchContext,
  ): Promise<AgentSession> {
    const claudeConfig = this.assertConfig(config);
    return new ClaudeAgentSession(claudeConfig, {
      defaults: this.defaults,
      runtimeSettings: this.runtimeSettings,
      launchEnv: launchContext?.env,
      chromeEnabled: launchContext?.chromeEnabled,
      playwrightEnabled: launchContext?.playwrightEnabled,
      logger: this.logger,
      queryFactory: this.queryFactory,
    });
  }

  async resumeSession(
    handle: AgentPersistenceHandle,
    overrides?: Partial<AgentSessionConfig>,
    launchContext?: AgentLaunchContext,
  ): Promise<AgentSession> {
    const metadata = coerceSessionMetadata(handle.metadata);
    const merged: Partial<AgentSessionConfig> = { ...metadata, ...overrides };
    if (!merged.cwd) {
      throw new Error("Claude resume requires the original working directory in metadata");
    }
    const mergedConfig: AgentSessionConfig = { ...merged, provider: "claude", cwd: merged.cwd };
    const claudeConfig = this.assertConfig(mergedConfig);
    return new ClaudeAgentSession(claudeConfig, {
      defaults: this.defaults,
      runtimeSettings: this.runtimeSettings,
      handle,
      launchEnv: launchContext?.env,
      chromeEnabled: launchContext?.chromeEnabled,
      playwrightEnabled: launchContext?.playwrightEnabled,
      logger: this.logger,
      queryFactory: this.queryFactory,
    });
  }

  async listModels(_options?: ListModelsOptions): Promise<AgentModelDefinition[]> {
    return getClaudeModels();
  }

  async listPersistedAgents(
    options?: ListPersistedAgentsOptions,
  ): Promise<PersistedAgentDescriptor[]> {
    const configDir = process.env.CLAUDE_CONFIG_DIR ?? path.join(os.homedir(), ".claude");
    const projectsRoot = path.join(configDir, "projects");
    if (!(await pathExists(projectsRoot))) {
      return [];
    }
    const limit = options?.limit ?? 20;
    const candidates = await collectRecentClaudeSessions(projectsRoot, limit * 3);
    const descriptors: PersistedAgentDescriptor[] = [];

    for (const candidate of candidates) {
      const descriptor = await parseClaudeSessionDescriptor(candidate.path, candidate.mtime);
      if (descriptor) {
        descriptors.push(descriptor);
      }
      if (descriptors.length >= limit) {
        break;
      }
    }

    return descriptors;
  }

  async isAvailable(): Promise<boolean> {
    const command = this.runtimeSettings?.command;
    if (command?.mode === "replace") {
      return await isCommandAvailable(command.argv[0]);
    }
    // SDK ≥ 0.3.x spawns a per-platform native CLI binary from a sibling npm
    // package (@anthropic-ai/claude-agent-sdk-${platform}-${arch}). Verify it
    // resolves now so the diagnostic surfaces the install gap instead of
    // letting the user hit "Native CLI binary not found" mid-session.
    return resolveClaudeNativeBinary() !== null;
  }

  async getDiagnostic(): Promise<{ diagnostic: string }> {
    try {
      const resolvedBinary =
        resolveClaudeNativeBinary() ?? (await findExecutable("claude")) ?? "not found";
      const available = await this.isAvailable();
      const version = await resolveClaudeVersion(this.runtimeSettings);
      const auth = available ? await resolveClaudeAuth(this.runtimeSettings) : null;
      let modelsValue = "Not checked";
      let status = formatDiagnosticStatus(available);

      if (available) {
        try {
          const models = await this.listModels();
          modelsValue = String(models.length);
        } catch (error) {
          modelsValue = `Error - ${toDiagnosticErrorMessage(error)}`;
          status = formatDiagnosticStatus(available, {
            source: "model fetch",
            cause: error,
          });
        }
      }

      return {
        diagnostic: formatProviderDiagnostic("Claude Code", [
          { label: "Binary", value: resolvedBinary },
          ...(version ? [{ label: "Version", value: version }] : []),
          ...(auth ? [{ label: "Auth", value: auth }] : []),
          { label: "Models", value: modelsValue },
          { label: "Status", value: status },
        ]),
      };
    } catch (error) {
      return {
        diagnostic: formatProviderDiagnosticError("Claude Code", error),
      };
    }
  }

  private assertConfig(config: AgentSessionConfig): ClaudeAgentConfig {
    if (config.provider !== "claude") {
      throw new Error(`ClaudeAgentClient received config for provider '${config.provider}'`);
    }
    return { ...config, provider: "claude" } as ClaudeAgentConfig;
  }
}

async function resolveClaudeVersion(
  runtimeSettings?: ProviderRuntimeSettings,
): Promise<string | null> {
  const command = runtimeSettings?.command;

  try {
    if (command?.mode === "replace") {
      const { stdout } = await execCommand(
        command.argv[0]!,
        [...command.argv.slice(1), "--version"],
        { timeout: 5_000 },
      );
      return stdout.trim() || null;
    }

    const executable = await findExecutable("claude");
    if (!executable) {
      return null;
    }

    const { stdout } = await execCommand(executable, ["--version"], { timeout: 5_000 });
    return stdout.trim() || null;
  } catch {
    return null;
  }
}

async function resolveClaudeAuth(
  runtimeSettings?: ProviderRuntimeSettings,
): Promise<string | null> {
  const command = runtimeSettings?.command;

  const run = async (
    executable: string,
    args: string[],
  ): Promise<{ stdout: string; stderr: string }> => {
    try {
      return await execCommand(executable, args, { timeout: 5_000 });
    } catch (error) {
      const err = error as { stdout?: string; stderr?: string; message?: string };
      return {
        stdout: err.stdout ?? "",
        stderr: err.stderr ?? err.message ?? "",
      };
    }
  };

  try {
    let result: { stdout: string; stderr: string };
    if (command?.mode === "replace") {
      result = await run(command.argv[0]!, [...command.argv.slice(1), "auth", "status"]);
    } else {
      const executable = await findExecutable("claude");
      if (!executable) {
        return null;
      }
      result = await run(executable, ["auth", "status"]);
    }

    const combined = [result.stdout, result.stderr]
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join("\n");
    return combined || null;
  } catch {
    return null;
  }
}

function extractContextWindowSize(modelUsage: unknown): number | undefined {
  if (!modelUsage || typeof modelUsage !== "object") {
    return undefined;
  }

  let maxContextWindow: number | undefined;
  for (const value of Object.values(modelUsage as Record<string, unknown>)) {
    if (!value || typeof value !== "object") {
      continue;
    }
    const contextWindow = (value as { contextWindow?: unknown }).contextWindow;
    if (
      typeof contextWindow !== "number" ||
      !Number.isFinite(contextWindow) ||
      contextWindow <= 0
    ) {
      continue;
    }
    maxContextWindow = Math.max(maxContextWindow ?? 0, contextWindow);
  }

  return maxContextWindow;
}

function readStreamRequestInputTokens(event: Record<string, unknown>): number | undefined {
  const messageUsage = (event.message as { usage?: unknown } | undefined)?.usage;
  if (!messageUsage || typeof messageUsage !== "object") {
    return undefined;
  }
  const usage = messageUsage as {
    input_tokens?: unknown;
    cache_creation_input_tokens?: unknown;
    cache_read_input_tokens?: unknown;
  };
  const inputTokens =
    typeof usage.input_tokens === "number" && Number.isFinite(usage.input_tokens)
      ? usage.input_tokens
      : undefined;
  const cacheCreationInputTokens =
    typeof usage.cache_creation_input_tokens === "number" &&
    Number.isFinite(usage.cache_creation_input_tokens)
      ? usage.cache_creation_input_tokens
      : 0;
  const cacheReadInputTokens =
    typeof usage.cache_read_input_tokens === "number" &&
    Number.isFinite(usage.cache_read_input_tokens)
      ? usage.cache_read_input_tokens
      : 0;
  if (typeof inputTokens !== "number" || inputTokens < 0) {
    return undefined;
  }
  return inputTokens + cacheCreationInputTokens + cacheReadInputTokens;
}

function readStreamRequestOutputTokens(event: Record<string, unknown>): number | undefined {
  const outputTokens = (event.usage as { output_tokens?: unknown } | undefined)?.output_tokens;
  if (typeof outputTokens !== "number" || !Number.isFinite(outputTokens) || outputTokens < 0) {
    return undefined;
  }
  return outputTokens;
}

class ClaudeAgentSession implements AgentSession {
  readonly provider: "claude" = "claude";
  readonly capabilities = CLAUDE_CAPABILITIES;

  private readonly config: ClaudeAgentConfig;
  private readonly launchEnv?: Record<string, string>;
  private readonly chromeEnabled: boolean;
  private readonly playwrightEnabled: boolean;
  private readonly defaults?: { agents?: Record<string, AgentDefinition> };
  private readonly runtimeSettings?: ProviderRuntimeSettings;
  private readonly logger: Logger;
  private readonly queryFactory: typeof query;
  private query: Query | null = null;
  private input: AsyncMessageInput<SDKUserMessage> | null = null;
  private claudeSessionId: string | null;
  private persistence: AgentPersistenceHandle | null;
  private currentMode: PermissionMode;
  private planResumeMode: PermissionMode | null = null;
  private availableModes: AgentMode[] = DEFAULT_MODES;
  private toolUseCache = new Map<string, ToolUseCacheEntry>();
  private toolUseIndexToId = new Map<number, string>();
  private toolUseInputBuffers = new Map<string, string>();
  // Monotonic sequence per toolUseId for streaming file_chunk events. Lets
  // the client discard out-of-order chunks if Zod parse latency reorders
  // them on arrival. Cleared when the tool use completes.
  private fileChunkSequences = new Map<string, number>();
  // Tracks the last file_path a streaming tool call was known to target
  // so we can emit a final `isComplete: true` chunk on content_block_stop
  // even if the terminating delta didn't carry a fresh file_path reading.
  private fileChunkFilePaths = new Map<string, string>();
  // Last progressive content emitted per tool use — dedupes re-emissions
  // when the JSON buffer grows but the extracted content hasn't (e.g.
  // keys / whitespace added after the content field). Keys cleared in
  // finalizeFileChunkStream / flushPendingToolCalls.
  private fileChunkLastContent = new Map<string, string>();
  private pendingPermissions = new Map<string, PendingPermission>();
  private activeForegroundTurnId: string | null = null;
  private autonomousTurn: AutonomousTurnState | null = null;
  private readonly subscribers = new Set<(event: AgentStreamEvent) => void>();
  private readonly timelineAssembler = new TimelineAssembler();
  private readonly sidechainTracker = new ClaudeSidechainTracker({
    getToolInput: (toolUseId) => this.toolUseCache.get(toolUseId)?.input ?? null,
  });
  private persistedHistory: AgentTimelineItem[] = [];
  private historyPending = false;
  private turnState: TurnState = "idle";
  private nextTurnOrdinal = 1;
  private cancelCurrentTurn: (() => void) | null = null;
  private cachedRuntimeInfo: AgentRuntimeInfo | null = null;
  private lastOptionsModel: string | null = null;
  private lastRuntimeModel: string | null = null;
  private compacting = false;
  private queryPumpPromise: Promise<void> | null = null;
  private queryRestartNeeded = false;
  private pendingInterruptAbort = false;
  private lastForegroundPromptText: string | null = null;
  private foregroundHasVisibleActivity = false;
  private lastContextWindowUsedTokens: number | undefined;
  private lastContextWindowMaxTokens: number | undefined;
  private lastStreamRequestInputTokens: number | undefined;
  private lastStreamRequestOutputTokens: number | undefined;
  private lastRateLimits: AgentRateLimit[] | undefined;
  private userMessageIds: string[] = [];
  private recentStderr = "";
  private closed = false;
  private fastModeEnabled = false;
  private usageProbeUnsubscribe: (() => void) | null = null;

  constructor(config: ClaudeAgentConfig, options: ClaudeAgentSessionOptions) {
    this.config = config;
    this.launchEnv = options.launchEnv;
    this.chromeEnabled = options.chromeEnabled ?? true;
    this.playwrightEnabled = options.playwrightEnabled ?? true;
    this.defaults = options.defaults;
    this.runtimeSettings = options.runtimeSettings;
    this.logger = options.logger;
    this.queryFactory = options.queryFactory ?? query;
    const handle = options.handle;

    if (handle) {
      if (!handle.sessionId) {
        throw new Error("Cannot resume: persistence handle has no sessionId");
      }
      this.claudeSessionId = handle.sessionId;
      this.persistence = handle;
      this.loadPersistedHistory(handle.sessionId);
    } else {
      this.claudeSessionId = null;
      this.persistence = null;
    }

    // Validate mode if provided
    if (config.modeId && !VALID_CLAUDE_MODES.has(config.modeId)) {
      const validModesList = Array.from(VALID_CLAUDE_MODES).join(", ");
      throw new Error(
        `Invalid mode '${config.modeId}' for Claude provider. Valid modes: ${validModesList}`,
      );
    }

    this.currentMode = isPermissionMode(config.modeId) ? config.modeId : "default";
    if (this.currentMode !== "plan") {
      this.planResumeMode = this.currentMode;
    }

    this.fastModeEnabled = Boolean(config.featureValues?.fast_mode);

    // Subscribe to the daemon-wide usage probe (see ./claude/usage-probe.ts).
    // The probe is the only public surface that reports 5h/7d plan utilization
    // at all usage levels. If the probe is up (singleton started in
    // bootstrap), this session will receive an immediate sync of the current
    // cached rate-limits and a callback whenever the probe refreshes.
    const probe = getClaudeUsageProbe();
    if (probe) {
      this.usageProbeUnsubscribe = probe.subscribe((rateLimits) => {
        this.applyUsageProbeRateLimits(rateLimits);
      });
    }
  }

  private applyUsageProbeRateLimits(rateLimits: AgentRateLimit[]): void {
    if (this.closed) return;
    // Merge by rateLimitType so any buckets we learned from
    // `rate_limit_event` (e.g. overage) survive — the probe only ships
    // five_hour / seven_day.
    const merged = new Map<string, AgentRateLimit>();
    for (const entry of this.lastRateLimits ?? []) {
      merged.set(entry.rateLimitType, entry);
    }
    for (const entry of rateLimits) {
      merged.set(entry.rateLimitType, entry);
    }
    this.lastRateLimits = Array.from(merged.values());
    const usage: AgentUsage = { rateLimits: this.lastRateLimits };
    if (this.lastContextWindowMaxTokens !== undefined) {
      usage.contextWindowMaxTokens = this.lastContextWindowMaxTokens;
    }
    if (this.lastContextWindowUsedTokens !== undefined) {
      usage.contextWindowUsedTokens = this.lastContextWindowUsedTokens;
    }
    this.dispatchEvents([{ type: "usage_updated", provider: "claude", usage }]);
  }

  get id(): string | null {
    return this.claudeSessionId;
  }

  get features(): AgentFeature[] {
    return buildClaudeFeatures({
      modelId: this.config.model,
      fastModeEnabled: this.fastModeEnabled,
    });
  }

  async getRuntimeInfo(): Promise<AgentRuntimeInfo> {
    if (this.cachedRuntimeInfo) {
      return { ...this.cachedRuntimeInfo };
    }
    const info: AgentRuntimeInfo = {
      provider: "claude",
      sessionId: this.claudeSessionId,
      model: this.lastOptionsModel,
      modeId: this.currentMode ?? null,
      ...(this.lastRuntimeModel
        ? {
            extra: {
              runtimeModel: this.lastRuntimeModel,
            },
          }
        : {}),
    };
    this.cachedRuntimeInfo = info;
    return { ...info };
  }

  async run(prompt: AgentPromptInput, options?: AgentRunOptions): Promise<AgentRunResult> {
    const timeline: AgentTimelineItem[] = [];
    let finalText = "";
    let usage: AgentUsage | undefined;
    let turnId: string | null = null;
    const bufferedEvents: AgentStreamEvent[] = [];
    let settled = false;
    let resolveCompletion!: () => void;
    let rejectCompletion!: (error: Error) => void;

    const processEvent = (event: AgentStreamEvent) => {
      if (settled) {
        return;
      }
      const eventTurnId = (event as { turnId?: string }).turnId;
      if (turnId && eventTurnId && eventTurnId !== turnId) {
        return;
      }
      if (event.type === "timeline") {
        timeline.push(event.item);
        if (event.item.type === "assistant_message") {
          if (!finalText) {
            finalText = event.item.text;
          } else if (event.item.text.startsWith(finalText)) {
            finalText = event.item.text;
          } else {
            finalText += event.item.text;
          }
        }
        return;
      }
      if (event.type === "turn_completed") {
        usage = event.usage;
        settled = true;
        resolveCompletion();
        return;
      }
      if (event.type === "turn_failed") {
        settled = true;
        rejectCompletion(new Error(event.error));
        return;
      }
      if (event.type === "turn_canceled") {
        settled = true;
        resolveCompletion();
      }
    };

    const completion = new Promise<void>((resolve, reject) => {
      resolveCompletion = resolve;
      rejectCompletion = reject;
    });
    const unsubscribe = this.subscribe((event) => {
      if (!turnId) {
        bufferedEvents.push(event);
        return;
      }
      processEvent(event);
    });

    try {
      const result = await this.startTurn(prompt, options);
      turnId = result.turnId;
      for (const event of bufferedEvents) {
        processEvent(event);
      }
      if (!settled) {
        await completion;
      }
    } finally {
      unsubscribe();
    }

    this.cachedRuntimeInfo = {
      provider: "claude",
      sessionId: this.claudeSessionId,
      model: this.lastOptionsModel,
      modeId: this.currentMode ?? null,
    };

    if (!this.claudeSessionId) {
      throw new Error("Session ID not set after run completed");
    }

    return {
      sessionId: this.claudeSessionId,
      finalText,
      usage,
      timeline,
    };
  }

  async startTurn(
    prompt: AgentPromptInput,
    _options?: AgentRunOptions,
  ): Promise<{ turnId: string }> {
    if (this.closed) {
      throw new Error("Claude session is closed");
    }
    if (this.activeForegroundTurnId) {
      throw new Error("A foreground turn is already active");
    }

    const slashCommand = this.resolveSlashCommandInvocation(prompt);
    if (slashCommand?.commandName === REWIND_COMMAND_NAME) {
      const turnId = this.createTurnId("foreground");
      this.activeForegroundTurnId = turnId;
      this.transitionTurnState("foreground", "rewind command");
      void this.executeRewindTurn(turnId, slashCommand);
      return { turnId };
    }

    if (this.autonomousTurn) {
      this.completeAutonomousTurn();
    }

    const sdkMessage = this.toSdkUserMessage(prompt);
    this.lastForegroundPromptText = this.extractPromptText(prompt);
    const turnId = this.createTurnId("foreground");
    this.activeForegroundTurnId = turnId;
    this.foregroundHasVisibleActivity = false;
    this.transitionTurnState("foreground", "foreground turn started");
    this.clearRecentStderr();

    let cancelIssued = false;
    const requestCancel = () => {
      if (cancelIssued) {
        return;
      }
      cancelIssued = true;
      if (this.cancelCurrentTurn === requestCancel) {
        this.cancelCurrentTurn = null;
      }
      this.rejectAllPendingPermissions(new Error("Permission request aborted"));
      this.finishForegroundTurn({
        type: "turn_canceled",
        provider: "claude",
        reason: "Interrupted",
      });
      void this.interruptActiveTurn().catch((error) => {
        this.logger.warn({ err: error }, "Failed to interrupt during cancel");
      });
    };
    this.cancelCurrentTurn = requestCancel;

    this.notifySubscribers({ type: "turn_started", provider: "claude" });

    try {
      await this.ensureQuery();
      if (!this.input) {
        throw new Error("Claude session input stream not initialized");
      }
      this.startQueryPump();
      this.input.push(sdkMessage);
    } catch (error) {
      this.finishForegroundTurn(
        this.buildTurnFailedEvent(error instanceof Error ? error.message : "Claude stream failed"),
      );
    }

    return { turnId };
  }

  subscribe(callback: (event: AgentStreamEvent) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  async interrupt(): Promise<void> {
    if (this.cancelCurrentTurn) {
      this.cancelCurrentTurn();
      return;
    }

    if (this.autonomousTurn) {
      this.flushPendingToolCalls();
      this.completeAutonomousTurn();
    }

    await this.interruptActiveTurn();
  }

  async *streamHistory(): AsyncGenerator<AgentStreamEvent> {
    if (!this.historyPending || this.persistedHistory.length === 0) {
      return;
    }
    const history = this.persistedHistory;
    this.persistedHistory = [];
    this.historyPending = false;
    for (const item of history) {
      yield { type: "timeline", item, provider: "claude" };
    }
  }

  async getAvailableModes(): Promise<AgentMode[]> {
    return this.availableModes;
  }

  async getCurrentMode(): Promise<string | null> {
    return this.currentMode ?? null;
  }

  async setMode(modeId: string): Promise<void> {
    // Validate mode
    if (!VALID_CLAUDE_MODES.has(modeId)) {
      const validModesList = Array.from(VALID_CLAUDE_MODES).join(", ");
      throw new Error(
        `Invalid mode '${modeId}' for Claude provider. Valid modes: ${validModesList}`,
      );
    }

    const normalized = isPermissionMode(modeId) ? modeId : "default";
    const previousMode = this.currentMode;
    // Update the daemon-side mirror BEFORE awaiting the SDK. Any canUseTool
    // callback that fires inside the await window must observe the new mode,
    // otherwise toggling (e.g. default → bypassPermissions) leaks a stale
    // permission prompt for in-flight tool calls.
    if (normalized === "plan") {
      if (previousMode !== "plan") {
        this.planResumeMode = previousMode;
      }
    } else {
      this.planResumeMode = normalized;
    }
    this.currentMode = normalized;
    const query = await this.ensureQuery();
    await query.setPermissionMode(normalized);
  }

  async setModel(modelId: string | null): Promise<void> {
    const normalizedModelId =
      typeof modelId === "string" && modelId.trim().length > 0 ? modelId : null;
    const query = await this.ensureQuery();
    await query.setModel(normalizedModelId ?? undefined);
    this.config.model = normalizedModelId ?? undefined;
    this.lastOptionsModel = normalizedModelId ?? this.lastOptionsModel;
    this.lastRuntimeModel = null;
    this.cachedRuntimeInfo = null;
    // Model change affects persistence metadata, so invalidate cached handle.
    this.persistence = null;
  }

  async setThinkingOption(thinkingOptionId: string | null): Promise<void> {
    const normalizedThinkingOptionId =
      typeof thinkingOptionId === "string" && thinkingOptionId.trim().length > 0
        ? thinkingOptionId
        : null;

    if (!normalizedThinkingOptionId || normalizedThinkingOptionId === "default") {
      this.config.thinkingOptionId = undefined;
    } else if (isClaudeThinkingEffort(normalizedThinkingOptionId)) {
      this.config.thinkingOptionId = normalizedThinkingOptionId;
    } else {
      throw new Error(`Unknown thinking option: ${normalizedThinkingOptionId}`);
    }
    this.queryRestartNeeded = true;
  }

  async setFeature(featureId: string, value: unknown): Promise<void> {
    if (featureId === "fast_mode") {
      const next = Boolean(value);
      if (next === this.fastModeEnabled) {
        return;
      }
      this.fastModeEnabled = next;
      this.config.featureValues = {
        ...(this.config.featureValues ?? {}),
        fast_mode: next,
      };
      this.cachedRuntimeInfo = null;
      // Apply live to the running Claude session via the SDK's flag-settings
      // control plane — documented as the mid-session equivalent of the
      // `settings` option on query() (sdk.d.ts: applyFlagSettings). No restart
      // needed; streaming-input only, which is the mode we always run in.
      //
      // If there's no live query (idle agent — pump's finally nullifies
      // this.query between turns), the next spawn picks up the flag via the
      // `settings: { fastMode }` injection in buildOptions().
      if (this.query?.applyFlagSettings) {
        await this.query.applyFlagSettings({ fastMode: next });
        // Read-back via the SDK control plane to confirm the binary applied
        // the setting we just sent. `getSettings` exists on the SDK Query
        // implementation but isn't on its public TS surface (sdk.d.ts), so
        // probe for it defensively. Logged at info level so a single tail
        // of daemon.log proves the toggle landed.
        const queryWithGetSettings = this.query as {
          getSettings?: () => Promise<unknown>;
        };
        if (typeof queryWithGetSettings.getSettings === "function") {
          try {
            const effective = await queryWithGetSettings.getSettings();
            const observedFastMode =
              effective &&
              typeof effective === "object" &&
              "fastMode" in effective &&
              typeof (effective as { fastMode?: unknown }).fastMode === "boolean"
                ? (effective as { fastMode: boolean }).fastMode
                : null;
            this.logger.info(
              { featureId, requested: next, observed: observedFastMode },
              "claude: fast_mode read-back from SDK",
            );
          } catch (error) {
            this.logger.warn(
              { err: error, featureId },
              "claude: fast_mode getSettings read-back failed",
            );
          }
        }
      }
      return;
    }
    throw new Error(`Unknown feature: ${featureId}`);
  }

  getPendingPermissions(): AgentPermissionRequest[] {
    return Array.from(this.pendingPermissions.values()).map((entry) => entry.request);
  }

  async respondToPermission(requestId: string, response: AgentPermissionResponse): Promise<void> {
    const pending = this.pendingPermissions.get(requestId);
    if (!pending) {
      throw new Error(`No pending permission request with id '${requestId}'`);
    }
    this.pendingPermissions.delete(requestId);
    pending.cleanup?.();

    if (response.behavior === "allow") {
      if (pending.request.kind === "plan") {
        const selectedActionId = response.selectedActionId;
        const shouldResumePriorMode =
          selectedActionId === "implement_resume" && this.planResumeMode === "bypassPermissions";
        const targetMode: PermissionMode = shouldResumePriorMode
          ? "bypassPermissions"
          : "acceptEdits";
        await this.setMode(targetMode);
        this.pushToolCall(
          mapClaudeCompletedToolCall({
            name: "plan_approval",
            callId: pending.request.id,
            input: pending.request.input ?? null,
            output: {
              approved: true,
              actionId: selectedActionId ?? "implement",
            },
          }),
        );
      }
      const resolvedInput = response.updatedInput ?? pending.request.input ?? {};
      // For question permissions, merge the answers back into the tool use
      // cache so handleToolResult emits them in the completion event. Without
      // this the completed tool_call detail only has the original questions and
      // AskUserQuestionAnswersCard receives no answers to render.
      if (pending.request.kind === "question") {
        const toolUseId =
          typeof pending.request.metadata?.toolUseId === "string"
            ? pending.request.metadata.toolUseId
            : null;
        if (toolUseId) {
          const entry = this.toolUseCache.get(toolUseId);
          if (entry) {
            this.toolUseCache.set(toolUseId, { ...entry, input: resolvedInput });
          }
        }
      }
      const result: PermissionResult = {
        behavior: "allow",
        updatedInput: resolvedInput,
        updatedPermissions: this.normalizePermissionUpdates(response.updatedPermissions),
      };
      pending.resolve(result);
    } else {
      if (pending.request.kind === "tool") {
        this.pushToolCall(
          mapClaudeFailedToolCall({
            name: pending.request.name,
            callId:
              (typeof pending.request.metadata?.toolUseId === "string"
                ? pending.request.metadata.toolUseId
                : null) ?? pending.request.id,
            input: pending.request.input ?? null,
            output: null,
            error: { message: response.message ?? "Permission denied" },
          }),
        );
      }
      const result: PermissionResult = {
        behavior: "deny",
        message: response.message ?? "Permission request denied",
        interrupt: response.interrupt,
      };
      pending.resolve(result);
    }

    this.pushEvent({
      type: "permission_resolved",
      provider: "claude",
      requestId,
      resolution: response,
    });
  }

  describePersistence(): AgentPersistenceHandle | null {
    if (this.persistence) {
      return this.persistence;
    }
    if (!this.claudeSessionId) {
      return null;
    }
    this.persistence = {
      provider: "claude",
      sessionId: this.claudeSessionId,
      nativeHandle: this.claudeSessionId,
      metadata: { ...this.config },
    };
    return this.persistence;
  }

  async close(): Promise<void> {
    this.logger.trace(
      {
        claudeSessionId: this.claudeSessionId,
        turnState: this.turnState,
        hasQuery: Boolean(this.query),
        hasInput: Boolean(this.input),
        hasActiveForegroundTurnId: Boolean(this.activeForegroundTurnId),
      },
      "Claude session close: start",
    );
    this.closed = true;
    if (this.usageProbeUnsubscribe) {
      try {
        this.usageProbeUnsubscribe();
      } catch {
        // ignore
      }
      this.usageProbeUnsubscribe = null;
    }
    this.rejectAllPendingPermissions(new Error("Claude session closed"));
    this.cancelCurrentTurn?.();
    this.subscribers.clear();
    this.activeForegroundTurnId = null;
    this.autonomousTurn = null;
    this.cancelCurrentTurn = null;
    this.turnState = "idle";
    this.sidechainTracker.clear();
    this.input?.end();
    this.query?.close?.();
    await this.awaitWithTimeout(this.query?.interrupt?.(), "close query interrupt");
    await this.awaitWithTimeout(this.query?.return?.(), "close query return");
    this.query = null;
    this.input = null;
    this.logger.trace(
      { claudeSessionId: this.claudeSessionId, turnState: this.turnState },
      "Claude session close: completed",
    );
  }

  async listCommands(): Promise<AgentSlashCommand[]> {
    const q = await this.ensureQuery();
    const commands = await q.supportedCommands();
    const commandMap = new Map<string, AgentSlashCommand>();
    for (const cmd of commands) {
      if (!commandMap.has(cmd.name)) {
        commandMap.set(cmd.name, {
          name: cmd.name,
          description: cmd.description,
          argumentHint: cmd.argumentHint,
        });
      }
    }
    if (!commandMap.has(REWIND_COMMAND_NAME)) {
      commandMap.set(REWIND_COMMAND_NAME, REWIND_COMMAND);
    }
    return Array.from(commandMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  private resolveSlashCommandInvocation(prompt: AgentPromptInput): SlashCommandInvocation | null {
    if (typeof prompt !== "string") {
      return null;
    }
    const parsed = this.parseSlashCommandInput(prompt);
    if (!parsed) {
      return null;
    }
    return parsed.commandName === REWIND_COMMAND_NAME ? parsed : null;
  }

  private parseSlashCommandInput(text: string): SlashCommandInvocation | null {
    const trimmed = text.trim();
    if (!trimmed.startsWith("/") || trimmed.length <= 1) {
      return null;
    }
    const withoutPrefix = trimmed.slice(1);
    const firstWhitespaceIdx = withoutPrefix.search(/\s/);
    const commandName =
      firstWhitespaceIdx === -1 ? withoutPrefix : withoutPrefix.slice(0, firstWhitespaceIdx);
    if (!commandName || commandName.includes("/")) {
      return null;
    }
    const rawArgs =
      firstWhitespaceIdx === -1 ? "" : withoutPrefix.slice(firstWhitespaceIdx + 1).trim();
    return rawArgs.length > 0
      ? { commandName, args: rawArgs, rawInput: trimmed }
      : { commandName, rawInput: trimmed };
  }

  private buildRewindSuccessMessage(
    targetUserMessageId: string,
    rewindResult: { filesChanged?: string[]; insertions?: number; deletions?: number },
  ): string {
    const fileCount = Array.isArray(rewindResult.filesChanged)
      ? rewindResult.filesChanged.length
      : undefined;
    const stats: string[] = [];
    if (typeof fileCount === "number") {
      stats.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);
    }
    if (typeof rewindResult.insertions === "number") {
      stats.push(`${rewindResult.insertions} insertions`);
    }
    if (typeof rewindResult.deletions === "number") {
      stats.push(`${rewindResult.deletions} deletions`);
    }
    if (stats.length > 0) {
      return `Rewound tracked files to message ${targetUserMessageId} (${stats.join(", ")}).`;
    }
    return `Rewound tracked files to message ${targetUserMessageId}.`;
  }

  private async attemptRewind(args: string | undefined): Promise<{
    messageId: string | null;
    result?: { filesChanged?: string[]; insertions?: number; deletions?: number };
    error?: string;
  }> {
    if (typeof args === "string" && args.trim().length > 0) {
      const candidate = args.trim().split(/\s+/)[0] ?? "";
      if (!UUID_PATTERN.test(candidate)) {
        return {
          messageId: null,
          error: "Invalid message UUID. Usage: /rewind <user_message_uuid> or /rewind",
        };
      }
      const rewindResult = await this.rewindFilesOnce(candidate);
      if (rewindResult.canRewind) {
        return { messageId: candidate, result: rewindResult };
      }
      return {
        messageId: null,
        error: rewindResult.error ?? `No file checkpoint found for message ${candidate}.`,
      };
    }

    const candidates = this.getRewindCandidateUserMessageIds();
    if (candidates.length === 0) {
      return {
        messageId: null,
        error: "No prior user message available to rewind. Use /rewind <user_message_uuid>.",
      };
    }

    let lastError: string | undefined;
    for (const candidate of candidates) {
      try {
        const rewindResult = await this.rewindFilesOnce(candidate);
        if (rewindResult.canRewind) {
          return { messageId: candidate, result: rewindResult };
        }
        if (rewindResult.error) {
          lastError = rewindResult.error;
        }
      } catch (error) {
        lastError = error instanceof Error ? error.message : "Failed to rewind tracked files.";
      }
    }

    return {
      messageId: null,
      error: lastError ?? "No rewind checkpoints are currently available for this session.",
    };
  }

  private async rewindFilesOnce(messageId: string): Promise<{
    canRewind: boolean;
    error?: string;
    filesChanged?: string[];
    insertions?: number;
    deletions?: number;
  }> {
    try {
      const query = await this.ensureFreshQuery();
      return await query.rewindFiles(messageId, { dryRun: false });
    } catch (error) {
      // The Claude SDK transport can close after a rewind call.
      // If that happens, mark the query stale so a follow-up attempt uses a fresh query.
      this.queryRestartNeeded = true;
      throw error;
    }
  }

  private async ensureFreshQuery(): Promise<Query> {
    if (this.query) {
      this.queryRestartNeeded = true;
    }
    return this.ensureQuery();
  }

  private getRewindCandidateUserMessageIds(): string[] {
    const candidates: string[] = [];
    const pushUnique = (value: string | null | undefined) => {
      if (typeof value === "string" && value.length > 0 && !candidates.includes(value)) {
        candidates.push(value);
      }
    };

    const historyIds = this.readUserMessageIdsFromHistoryFile();
    for (let idx = historyIds.length - 1; idx >= 0; idx -= 1) {
      pushUnique(historyIds[idx]);
    }
    for (let idx = this.persistedHistory.length - 1; idx >= 0; idx -= 1) {
      const item = this.persistedHistory[idx];
      if (item?.type === "user_message") {
        pushUnique(item.messageId);
      }
    }
    for (let idx = this.userMessageIds.length - 1; idx >= 0; idx -= 1) {
      pushUnique(this.userMessageIds[idx]);
    }

    return candidates;
  }

  private readUserMessageIdsFromHistoryFile(): string[] {
    if (!this.claudeSessionId) {
      return [];
    }
    const historyPath = this.resolveHistoryPath(this.claudeSessionId);
    if (!historyPath || !fs.existsSync(historyPath)) {
      return [];
    }
    try {
      const ids: string[] = [];
      const content = fs.readFileSync(historyPath, "utf8");
      for (const line of content.split(/\n+/)) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          const entry = JSON.parse(trimmed);
          if (entry?.type === "user" && typeof entry.uuid === "string") {
            ids.push(entry.uuid);
          }
        } catch {
          // ignore malformed lines
        }
      }
      return ids;
    } catch {
      return [];
    }
  }

  private rememberUserMessageId(messageId: string | null | undefined): void {
    if (typeof messageId !== "string" || messageId.length === 0) {
      return;
    }
    const last = this.userMessageIds[this.userMessageIds.length - 1];
    if (last === messageId) {
      return;
    }
    this.userMessageIds.push(messageId);
  }

  private async ensureQuery(): Promise<Query> {
    if (this.query && !this.queryRestartNeeded) {
      return this.query;
    }

    if (this.queryRestartNeeded && this.query) {
      const oldQuery = this.query;
      const oldInput = this.input;
      // Null out query/input BEFORE awaiting the old iterator's return so the
      // old pump sees this.query !== activeQuery and skips failActiveTurns.
      this.query = null;
      this.input = null;
      this.queryPumpPromise = null;
      this.queryRestartNeeded = false;
      // Reset session identity for explicit restarts so the new query starts
      // a fresh session rather than resuming the previous one.
      this.claudeSessionId = null;
      oldInput?.end();
      oldQuery.close?.();
      try {
        await oldQuery.return?.();
      } catch {
        /* ignore */
      }
    }

    // When the pump died unexpectedly (query became null, e.g. after a session
    // ID overwrite error), preserve claudeSessionId so buildOptions() passes
    // resume: sessionId and the new query auto-resumes the previous session.
    // For explicit restarts above, claudeSessionId was already cleared.
    this.persistence = null;

    const input = createAsyncMessageInput<SDKUserMessage>();
    const options = await this.buildOptions();
    this.logger.debug({ options: summarizeClaudeOptionsForLog(options) }, "claude query");
    this.input = input;
    this.query = this.queryFactory({ prompt: input.iterable, options });
    // Do not kick off background control-plane queries here. Methods like
    // supportedCommands()/setPermissionMode() may execute immediately after
    // ensureQuery() (for listCommands()/setMode()), and sharing the same query
    // control plane can cause those calls to wait behind supportedModels().
    return this.query;
  }

  private async awaitWithTimeout(
    promise: Promise<unknown> | undefined,
    label: string,
  ): Promise<void> {
    if (!promise) {
      this.logger.trace({ label }, "Claude query operation skipped (no promise)");
      return;
    }
    const startedAt = Date.now();
    this.logger.trace({ label }, "Claude query operation wait start");
    try {
      await Promise.race([
        promise,
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error("timeout")), 3_000);
        }),
      ]);
      this.logger.trace(
        { label, durationMs: Date.now() - startedAt },
        "Claude query operation settled",
      );
    } catch (error) {
      this.logger.warn({ err: error, label }, "Claude query operation did not settle cleanly");
    }
  }

  private async buildOptions(): Promise<ClaudeOptions> {
    const thinkingOptionId =
      this.config.thinkingOptionId && this.config.thinkingOptionId !== "default"
        ? this.config.thinkingOptionId
        : undefined;
    let thinking: ClaudeOptions["thinking"];
    let effort: ClaudeOptions["effort"];
    if (thinkingOptionId && isClaudeThinkingEffort(thinkingOptionId)) {
      thinking = { type: "adaptive" };
      // SDK 0.2.71 types `effort` as 'low' | 'medium' | 'high' | 'max'; Opus 4.7
      // adds 'xhigh' which the binary accepts but the typings don't yet expose.
      effort = thinkingOptionId as ClaudeOptions["effort"];
    }

    const appendedSystemPrompt = [
      // Silence Claude Code's auto-attached malware-warning system-reminders so
      // sub-agents stop announcing them as suspected prompt injections. See
      // getSystemReminderGuidance for the full rationale.
      getSystemReminderGuidance(),

      composeAppostleSystemPrompt(this.config.systemPrompt),
    ]
      .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
      .join("\n\n");

    // ╔══════════════════════════════════════════════════════════════════╗
    // ║  CUSTOM SUBAGENTS — Claude auto-delegates based on description ║
    // ║  These supplement the built-in Task/Plan/Explore agents.       ║
    // ╚══════════════════════════════════════════════════════════════════╝
    const appostleAgents: Record<string, AgentDefinition> = {
      researcher: {
        description:
          "Use this agent when you need to explore unfamiliar code, trace data flow across modules, search broadly across the codebase, or read more than 2 files you haven't seen yet. Delegate investigation work here to keep the main conversation context lean.",
        prompt:
          "You are a codebase researcher. Your job is to investigate, trace, and report findings — never edit files. Read code, grep for patterns, follow imports, and build a clear picture. Report back a concise summary of what you found: key files, relevant code paths, and your conclusion. Keep your report under 300 words unless the investigation is complex.",
        model: "sonnet",
      },
      refactorer: {
        description:
          "Use this agent for refactors that touch more than 2 files — renames, migrations, pattern replacements, moving code between modules. Delegate multi-file changes here to isolate the blast radius.",
        prompt:
          "You are a refactoring specialist. Make the requested changes across all affected files. Be thorough — update imports, references, types, and tests. Run typecheck after changes if available. Report what you changed and any issues found.",
      },
      reviewer: {
        description:
          "Use this agent to review code for bugs, security issues, performance problems, or style violations. Use it before committing large changes or when the user asks for a review.",
        prompt:
          "You are a code reviewer. Analyze the code for correctness, security vulnerabilities (OWASP top 10), performance issues, and adherence to the project's coding standards. Be specific — cite file paths and line numbers. Flag severity: critical, warning, or nitpick. Keep the review focused and actionable.",
      },
      debugger: {
        description:
          "Use this agent to investigate bugs — read logs, trace error paths, check database state, reproduce issues. Delegate debugging here when the root cause isn't obvious from a quick look.",
        prompt:
          "You are a debugger. Your job is to find the root cause, not patch symptoms. Check real data first — logs, network requests, database state. Trace the full lifecycle of the bug. Present your findings: root cause, evidence, and a proposed minimal fix. Do not edit files unless explicitly asked.",
      },
    };

    const claudeBinary = await findExecutable("claude");
    this.logger.debug(
      {
        claudeBinary,
        pathEnvKey:
          process.env["Path"] !== undefined
            ? "Path"
            : process.env["PATH"] !== undefined
              ? "PATH"
              : null,
        pathIncludesClaudeLocalBin: (process.env["Path"] ?? process.env["PATH"] ?? "")
          .toLowerCase()
          .includes("\\.local\\bin"),
      },
      "Resolved Claude executable",
    );
    const base: ClaudeOptions = {
      cwd: this.config.cwd,
      includePartialMessages: true,
      permissionMode: this.currentMode,
      // Dynamic mode switching can recreate the underlying Claude query. Keep the
      // bypass launch capability available so later setPermissionMode("bypassPermissions")
      // calls do not fail after a model/thinking/rewind-driven restart.
      allowDangerouslySkipPermissions: true,
      agents: { ...appostleAgents, ...this.defaults?.agents },
      canUseTool: this.handlePermissionRequest,
      ...(claudeBinary ? { pathToClaudeCodeExecutable: claudeBinary } : {}),
      // Use Claude Code preset system prompt and load CLAUDE.md files
      // Append provider-agnostic system prompt and orchestrator instructions for agents.
      systemPrompt: {
        type: "preset",
        preset: "claude_code",
        append: appendedSystemPrompt,
      },
      settingSources: CLAUDE_SETTING_SOURCES,
      stderr: (data: string) => {
        this.captureStderr(data);
        this.logger.error({ stderr: data.trim() }, "Claude Agent SDK stderr");
      },
      env: {
        ...process.env,
        // Increase MCP timeouts for long-running tool calls (10 minutes)
        MCP_TIMEOUT: "600000",
        MCP_TOOL_TIMEOUT: "600000",
        ...(this.launchEnv ?? {}),
      },
      // Required for provider-level /rewind support.
      enableFileCheckpointing: true,
      // Enable Claude in Chrome integration so browser tools are available.
      // Can be disabled per-host via daemon config to prevent the client's
      // remote Chrome from being controlled on headless servers.
      ...(this.chromeEnabled ? { extraArgs: { chrome: null } } : {}),
      // If we have a session ID from a previous query (e.g., after interrupt),
      // resume that session to continue the conversation history.
      ...(this.claudeSessionId ? { resume: this.claudeSessionId } : {}),
      ...(thinking ? { thinking } : {}),
      ...(effort ? { effort } : {}),
      // Initial-spawn path for fast mode: bake the flag into the new
      // process's flag-settings layer. Mid-session toggles go through
      // setFeature → query.applyFlagSettings instead (no restart needed).
      // Also wire in the statusLine escape hatch (see statusline-relay.ts):
      // the CLI spawns this script after each turn with the documented
      // statusline JSON on stdin — the only public surface that ships
      // five_hour / seven_day utilization at all usage levels.
      // Initial-spawn path for fast mode: bake the flag into the new
      // process's flag-settings layer. Mid-session toggles go through
      // setFeature → query.applyFlagSettings instead (no restart needed).
      ...(this.fastModeEnabled ? { settings: { fastMode: true } } : {}),
      ...this.config.extra?.claude,
    };

    if (this.config.mcpServers) {
      base.mcpServers = this.normalizeMcpServers(this.config.mcpServers);
    } else {
      // Fallback: inherit MCP servers from ~/.claude.json so appostle-spawned
      // agents get the same tools the terminal `claude` CLI has configured.
      const homeMcps = loadHomeMcpServers(this.logger);
      if (homeMcps) {
        const filtered = this.playwrightEnabled
          ? homeMcps
          : Object.fromEntries(Object.entries(homeMcps).filter(([k]) => k !== "playwright"));
        base.mcpServers = this.normalizeMcpServers(filtered);
      }
    }

    if (this.config.model) {
      base.model = this.config.model;
      const modelBetas = getClaudeModelBetas(this.config.model);
      if (modelBetas?.length) {
        base.betas = [...(base.betas ?? []), ...(modelBetas as SdkBeta[])];
      }
    }
    this.lastOptionsModel = base.model ?? null;
    if (this.claudeSessionId) {
      base.resume = this.claudeSessionId;
    }
    if (this.config.allowedTools?.length) {
      base.allowedTools = [...(base.allowedTools ?? []), ...this.config.allowedTools];
    }
    if (this.runtimeSettings?.disallowedTools?.length) {
      base.disallowedTools = [
        ...(base.disallowedTools ?? []),
        ...this.runtimeSettings.disallowedTools,
      ];
    }
    return this.applyRuntimeSettings(base);
  }

  private applyRuntimeSettings(options: ClaudeOptions): ClaudeOptions {
    return applyRuntimeSettingsToClaudeOptions(options, this.runtimeSettings, this.launchEnv);
  }

  private normalizeMcpServers(
    servers: Record<string, McpServerConfig>,
  ): Record<string, ClaudeSdkMcpServerConfig> {
    const result: Record<string, ClaudeSdkMcpServerConfig> = {};
    for (const [name, config] of Object.entries(servers)) {
      result[name] = toClaudeSdkMcpConfig(config);
    }
    return result;
  }

  private toSdkUserMessage(prompt: AgentPromptInput): SDKUserMessage {
    const content: Array<
      | { type: "text"; text: string }
      | {
          type: "image";
          source: {
            type: "base64";
            media_type: "image/jpeg" | "image/png" | "image/gif" | "image/webp";
            data: string;
          };
        }
    > = [];
    if (Array.isArray(prompt)) {
      for (const chunk of prompt) {
        if (chunk.type === "text") {
          // Skip empty text blocks. Anthropic adds `cache_control` markers
          // at fixed breakpoints in the conversation; if a breakpoint lands
          // on an empty text block the API rejects the entire request
          // ("cache_control cannot be set for empty text blocks"). One
          // empty block lingering in conversation memory pollutes every
          // future turn until the agent is forked or reset.
          if (chunk.text.length > 0) {
            content.push({ type: "text", text: chunk.text });
          }
        } else if (chunk.type === "image") {
          content.push({
            type: "image",
            source: {
              type: "base64",
              media_type: chunk.mimeType as "image/jpeg" | "image/png" | "image/gif" | "image/webp",
              data: chunk.data,
            },
          });
        } else if (
          chunk.type === "github_pr" ||
          chunk.type === "github_issue" ||
          chunk.type === "gitlab_mr" ||
          chunk.type === "gitlab_issue"
        ) {
          const rendered = renderPromptAttachmentAsText(chunk);
          if (rendered.length > 0) {
            content.push({ type: "text", text: rendered });
          }
        }
      }
    } else if (prompt.length > 0) {
      // Same guard for the bare-string path. An empty `prompt` string would
      // otherwise become an empty text block in conversation memory.
      content.push({ type: "text", text: prompt });
    }
    // Defensive fallback: API rejects user messages with empty content arrays.
    // If everything we received was empty (e.g. an image-only send where the
    // image block already lives in the SDK's stored memory from a prior turn)
    // we add a single benign text block so the message has at least one
    // entry. This should be unreachable in normal flow.
    if (content.length === 0) {
      content.push({ type: "text", text: "(empty message)" });
    }

    const messageId = randomUUID();
    this.rememberUserMessageId(messageId);

    return {
      type: "user",
      message: {
        role: "user",
        content,
      },
      parent_tool_use_id: null,
      uuid: messageId,
      session_id: this.claudeSessionId ?? "",
    };
  }

  private transitionTurnState(next: TurnState, reason: string): void {
    if (this.turnState === next) {
      return;
    }
    this.logger.debug({ from: this.turnState, to: next, reason }, "Claude turn state transition");
    this.turnState = next;
  }

  private syncTurnState(reason: string): void {
    if (this.activeForegroundTurnId) {
      this.transitionTurnState("foreground", reason);
      return;
    }
    if (this.autonomousTurn) {
      this.transitionTurnState("autonomous", reason);
      return;
    }
    this.transitionTurnState("idle", reason);
  }

  private isAbortError(message: SDKMessage): boolean {
    const errors = "errors" in message && Array.isArray(message.errors) ? message.errors : [];
    return errors.some((e: string) => /\baborted\b/i.test(e));
  }

  private buildTurnFailedEvent(
    errorMessage: string,
  ): Extract<AgentStreamEvent, { type: "turn_failed" }> {
    const normalized = errorMessage.trim() || "Claude run failed";
    const exitCodeMatch = normalized.match(/\bcode\s+(\d+)\b/i);
    const code = exitCodeMatch ? exitCodeMatch[1] : undefined;
    const diagnostic = this.getRecentStderrDiagnostic();
    return {
      type: "turn_failed",
      provider: "claude",
      error: normalized,
      ...(code ? { code } : {}),
      ...(diagnostic ? { diagnostic } : {}),
    };
  }

  private captureStderr(data: string): void {
    const text = data.trim();
    if (!text) {
      return;
    }
    const combined = this.recentStderr ? `${this.recentStderr}\n${text}` : text;
    this.recentStderr = combined.slice(-MAX_RECENT_STDERR_CHARS);
  }

  private clearRecentStderr(): void {
    this.recentStderr = "";
  }

  private getRecentStderrDiagnostic(): string | undefined {
    return this.recentStderr.trim() || undefined;
  }

  private async awaitRecentStderrAfterProcessExit(error: unknown): Promise<void> {
    if (this.getRecentStderrDiagnostic()) {
      return;
    }
    const message = typeof error === "string" ? error : error instanceof Error ? error.message : "";
    if (
      !/\bprocess exited with code\b/i.test(message) &&
      !/\bterminated by signal\b/i.test(message)
    ) {
      return;
    }

    const startedAt = Date.now();
    while (!this.closed && !this.getRecentStderrDiagnostic()) {
      if (Date.now() - startedAt >= STDERR_FLUSH_WAIT_MS) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, STDERR_FLUSH_POLL_INTERVAL_MS));
    }
  }

  private createTurnId(owner: "foreground" | "autonomous"): string {
    return `${owner}-turn-${this.nextTurnOrdinal++}`;
  }

  private isTerminalTurnEvent(event: AgentStreamEvent): boolean {
    return (
      event.type === "turn_completed" ||
      event.type === "turn_failed" ||
      event.type === "turn_canceled"
    );
  }

  private extractPromptText(prompt: AgentPromptInput): string | null {
    if (typeof prompt === "string") {
      return prompt;
    }
    const textParts = prompt
      .filter((block): block is { type: "text"; text: string } => block.type === "text")
      .map((block) => block.text);
    return textParts.length > 0 ? textParts.join("\n") : null;
  }

  private async executeRewindTurn(
    _turnId: string,
    invocation: SlashCommandInvocation,
  ): Promise<void> {
    this.notifySubscribers({ type: "turn_started", provider: "claude" });
    try {
      const rewindAttempt = await this.attemptRewind(invocation.args);
      if (!rewindAttempt.messageId || !rewindAttempt.result) {
        this.finishForegroundTurn({
          type: "turn_failed",
          provider: "claude",
          error:
            rewindAttempt.error ??
            "No prior user message available to rewind. Use /rewind <user_message_uuid>.",
        });
        return;
      }
      this.notifySubscribers({
        type: "timeline",
        provider: "claude",
        item: {
          type: "assistant_message",
          text: this.buildRewindSuccessMessage(rewindAttempt.messageId, rewindAttempt.result),
        },
      });
      this.finishForegroundTurn({ type: "turn_completed", provider: "claude" });
    } catch (error) {
      this.finishForegroundTurn({
        type: "turn_failed",
        provider: "claude",
        error: error instanceof Error ? error.message : "Failed to rewind tracked files",
      });
    }
  }

  private shouldRecoverInterruptedQueryAbort(
    error: unknown,
    consecutiveRecoveries: number,
  ): boolean {
    if (consecutiveRecoveries >= 3) {
      return false;
    }
    const message =
      typeof error === "string"
        ? error
        : error instanceof Error
          ? `${error.message}\n${error.stack ?? ""}`
          : JSON.stringify(error);
    return message.toLowerCase().includes("request was aborted");
  }

  private finishForegroundTurn(
    event: Extract<AgentStreamEvent, { type: "turn_completed" | "turn_failed" | "turn_canceled" }>,
  ): void {
    if (event.type === "turn_failed" || event.type === "turn_canceled") {
      this.flushPendingToolCalls();
    }
    this.notifySubscribers(event);
    this.activeForegroundTurnId = null;
    this.lastForegroundPromptText = null;
    this.cancelCurrentTurn = null;
    this.syncTurnState("foreground turn terminal");
  }

  private dispatchEvents(events: AgentStreamEvent[]): void {
    let terminalSeen = false;
    for (const event of events) {
      this.notifySubscribers(event);
      terminalSeen ||= this.isTerminalTurnEvent(event);
    }

    if (terminalSeen) {
      if (this.activeForegroundTurnId) {
        this.activeForegroundTurnId = null;
        this.lastForegroundPromptText = null;
        this.cancelCurrentTurn = null;
        this.syncTurnState("foreground turn terminal");
      } else if (this.autonomousTurn) {
        this.autonomousTurn = null;
        this.syncTurnState("autonomous turn terminal");
      }
    }
  }

  private startAutonomousTurn(): void {
    if (this.autonomousTurn) {
      return;
    }
    this.autonomousTurn = {
      id: this.createTurnId("autonomous"),
    };
    this.notifySubscribers({ type: "turn_started", provider: "claude" });
    this.syncTurnState("autonomous turn started");
  }

  private completeAutonomousTurn(): void {
    if (!this.autonomousTurn) {
      return;
    }
    this.notifySubscribers({ type: "turn_completed", provider: "claude" });
    this.autonomousTurn = null;
    this.syncTurnState("autonomous turn completed");
  }

  private failActiveTurns(errorMessage: string): void {
    const failure = this.buildTurnFailedEvent(errorMessage);
    this.flushPendingToolCalls();
    if (this.activeForegroundTurnId) {
      this.finishForegroundTurn(failure);
      return;
    }
    if (this.autonomousTurn) {
      this.dispatchEvents([failure]);
    }
  }

  private startQueryPump(): void {
    if (this.closed || this.queryPumpPromise) {
      return;
    }

    const pump = this.runQueryPump().catch((error) => {
      this.logger.trace({ err: error }, "Claude query pump exited unexpectedly");
    });

    this.queryPumpPromise = pump;
    pump.finally(() => {
      if (this.queryPumpPromise === pump) {
        this.queryPumpPromise = null;
      }
    });
  }

  private async runQueryPump(): Promise<void> {
    let activeQuery: Query;
    try {
      activeQuery = await this.ensureQuery();
    } catch (error) {
      this.logger.trace({ err: error }, "Failed to initialize Claude query pump");
      this.failActiveTurns(error instanceof Error ? error.message : "Claude stream failed");
      return;
    }

    let consecutiveInterruptAbortRecoveries = 0;
    try {
      while (!this.closed && this.query === activeQuery) {
        try {
          for await (const message of activeQuery) {
            if (claudeDebug) {
              this.logger.trace(
                {
                  claudeSessionId: this.claudeSessionId,
                  messageType: message.type,
                  messageSubtype: "subtype" in message ? message.subtype : undefined,
                  messageUuid: "uuid" in message ? message.uuid : undefined,
                },
                "Claude query pump: raw SDK message",
              );
            }
            consecutiveInterruptAbortRecoveries = 0;
            if (await this.handleMissingResumedConversation(message, activeQuery)) {
              return;
            }
            this.routeSdkMessageFromPump(message);
          }
          if (!this.closed && this.query === activeQuery) {
            this.failActiveTurns("Claude stream ended before terminal result");
          }
          return;
        } catch (error) {
          if (
            !this.closed &&
            this.query === activeQuery &&
            this.shouldRecoverInterruptedQueryAbort(error, consecutiveInterruptAbortRecoveries)
          ) {
            consecutiveInterruptAbortRecoveries += 1;
            this.logger.debug(
              { recoveries: consecutiveInterruptAbortRecoveries },
              "Recovering Claude query pump after interrupt abort",
            );
            continue;
          }
          if (!this.closed && this.query === activeQuery) {
            await this.awaitRecentStderrAfterProcessExit(error);
            this.failActiveTurns(error instanceof Error ? error.message : "Claude stream failed");
          }
          return;
        }
      }
    } finally {
      if (this.query === activeQuery) {
        this.query = null;
        this.input = null;
      }
    }
  }

  private routeSdkMessageFromPump(message: SDKMessage): void {
    // Suppress stale results from interrupted requests. The cancel path already
    // emitted the terminal event; this result is leftover from the killed API
    // request. Consume the flag on ANY result so it doesn't linger.
    if (message.type === "result" && this.pendingInterruptAbort) {
      this.pendingInterruptAbort = false;
      if (message.subtype !== "success") {
        this.logger.debug("Suppressing stale non-success result from interrupted request");
        return;
      }
    }
    if (message.type === "result" && message.subtype !== "success" && this.isAbortError(message)) {
      this.logger.debug("Suppressing abort result by content");
      return;
    }

    const isForeground = Boolean(this.activeForegroundTurnId);
    const assistantishMessage =
      message.type === "assistant" ||
      message.type === "stream_event" ||
      message.type === "tool_progress" ||
      (message.type === "system" && message.subtype === "task_notification");

    if (!isForeground && assistantishMessage) {
      this.startAutonomousTurn();
    }
    if (!isForeground && !this.autonomousTurn && message.type === "result") {
      return;
    }

    const turnId = this.activeForegroundTurnId ?? this.autonomousTurn?.id ?? null;
    const identifiers = readEventIdentifiers(message);

    if (claudeDebug) {
      this.logger.trace(
        {
          claudeSessionId: this.claudeSessionId,
          messageType: message.type,
          turnId,
        },
        "Claude query pump: SDK message",
      );
    }

    const messageEvents = this.translateMessageToEvents(message, {
      suppressAssistantText: true,
      suppressReasoning: true,
    });
    const assistantTimelineEvents = this.timelineAssembler
      .consume({
        message,
        runId: turnId,
        messageIdHint: identifiers.messageId,
      })
      .map(
        (item) =>
          ({
            type: "timeline",
            item,
            provider: "claude",
          }) satisfies AgentStreamEvent,
      );

    // User message dedup: suppress echoed user messages that match the foreground prompt
    const filteredMessageEvents = messageEvents.filter((event) => {
      if (
        event.type === "timeline" &&
        event.item.type === "user_message" &&
        this.activeForegroundTurnId &&
        this.lastForegroundPromptText
      ) {
        if (event.item.text.trim() === this.lastForegroundPromptText.trim()) {
          return false;
        }
      }
      return true;
    });

    const events = [...filteredMessageEvents, ...assistantTimelineEvents];

    if (events.length === 0) {
      return;
    }

    if (
      this.pendingInterruptAbort &&
      message.type === "result" &&
      events.some((event) => event.type === "turn_completed" || event.type === "turn_failed") &&
      (!this.activeForegroundTurnId || !this.foregroundHasVisibleActivity)
    ) {
      this.pendingInterruptAbort = false;
      this.logger.debug("Suppressing stale Claude interrupt terminal result");
      return;
    }
    if (
      this.activeForegroundTurnId &&
      events.some(
        (event) =>
          event.type === "timeline" ||
          event.type === "permission_requested" ||
          event.type === "permission_resolved",
      )
    ) {
      this.foregroundHasVisibleActivity = true;
    }

    this.dispatchEvents(events);

    // After meaningful turn boundaries, ask the SDK directly for the true
    // context-window state via `query.getContextUsage()` (the SDK's pull-style
    // API). This is the canonical source — far more reliable than inferring
    // from stream events. We fire-and-forget; the poll dispatches its own
    // `usage_updated` event when it resolves.
    if (
      message.type === "system" &&
      (message.subtype === "init" || message.subtype === "compact_boundary")
    ) {
      void this.pollContextUsage();
    } else if (message.type === "result") {
      void this.pollContextUsage();
    }
  }

  private async pollContextUsage(): Promise<void> {
    const query = this.query;
    if (!query || typeof query.getContextUsage !== "function") {
      return;
    }
    try {
      const response = await query.getContextUsage();
      const totalTokens = response?.totalTokens;
      const maxTokens = response?.maxTokens ?? response?.rawMaxTokens;
      if (typeof maxTokens === "number" && Number.isFinite(maxTokens) && maxTokens > 0) {
        this.lastContextWindowMaxTokens = maxTokens;
      }
      if (typeof totalTokens === "number" && Number.isFinite(totalTokens) && totalTokens >= 0) {
        this.lastContextWindowUsedTokens = totalTokens;
      }
      if (
        this.lastContextWindowMaxTokens === undefined &&
        this.lastContextWindowUsedTokens === undefined
      ) {
        return;
      }
      const usage: AgentUsage = {};
      if (this.lastContextWindowMaxTokens !== undefined) {
        usage.contextWindowMaxTokens = this.lastContextWindowMaxTokens;
      }
      if (this.lastContextWindowUsedTokens !== undefined) {
        usage.contextWindowUsedTokens = this.lastContextWindowUsedTokens;
      }
      if (this.lastRateLimits !== undefined) {
        usage.rateLimits = this.lastRateLimits;
      }
      this.dispatchEvents([{ type: "usage_updated", provider: "claude", usage }]);
    } catch (error) {
      this.logger.debug(
        {
          module: "claude-agent",
          err: error,
        },
        "pollContextUsage failed",
      );
    }
  }

  private async handleMissingResumedConversation(
    message: SDKMessage,
    query: Query,
  ): Promise<boolean> {
    const staleResumeError = this.readMissingResumedConversationError(message);
    if (!staleResumeError) {
      return false;
    }

    this.logger.warn(
      {
        claudeSessionId: this.claudeSessionId,
        error: staleResumeError,
      },
      "Claude resumed session no longer exists; invalidating persisted session",
    );

    this.failActiveTurns(staleResumeError);
    this.input?.end();
    await this.awaitWithTimeout(
      query.return?.(),
      "query pump return on missing resumed conversation",
    );
    if (this.query === query) {
      this.query = null;
      this.input = null;
    }
    this.claudeSessionId = null;
    this.persistence = null;
    this.persistedHistory = [];
    this.historyPending = false;
    this.cachedRuntimeInfo = null;
    this.queryRestartNeeded = false;
    this.autonomousTurn = null;
    this.activeForegroundTurnId = null;
    this.syncTurnState("missing resumed conversation");
    return true;
  }

  private async interruptActiveTurn(): Promise<void> {
    const queryToInterrupt = this.query;
    if (!queryToInterrupt || typeof queryToInterrupt.interrupt !== "function") {
      this.logger.trace("interruptActiveTurn: no query to interrupt");
      return;
    }
    this.pendingInterruptAbort = true;
    try {
      await this.awaitWithTimeout(
        queryToInterrupt.interrupt(),
        "interruptActiveTurn query.interrupt()",
      );
    } catch (error) {
      this.logger.warn({ err: error }, "Failed to interrupt active turn");
    }
  }

  private translateMessageToEvents(
    message: SDKMessage,
    options?: {
      suppressAssistantText?: boolean;
      suppressReasoning?: boolean;
    },
  ): AgentStreamEvent[] {
    const parentToolUseId =
      "parent_tool_use_id" in message
        ? (message as { parent_tool_use_id: string | null }).parent_tool_use_id
        : null;
    if (parentToolUseId) {
      return this.sidechainTracker.handleMessage(message, parentToolUseId);
    }

    const events: AgentStreamEvent[] = [];
    const fallbackThreadSessionId = this.captureSessionIdFromMessage(message);
    if (fallbackThreadSessionId) {
      events.push({
        type: "thread_started",
        provider: "claude",
        sessionId: fallbackThreadSessionId,
      });
    }

    switch (message.type) {
      case "system":
        if (message.subtype === "init") {
          const threadSessionId = this.handleSystemMessage(message);
          if (threadSessionId) {
            events.push({
              type: "thread_started",
              provider: "claude",
              sessionId: threadSessionId,
            });
          }
        } else if (message.subtype === "status") {
          const status = (message as { status?: string }).status;
          if (status === "compacting") {
            this.compacting = true;
            events.push({
              type: "timeline",
              item: { type: "compaction", status: "loading" },
              provider: "claude",
            });
          }
          const compactResult = (message as { compact_result?: string }).compact_result;
          if (compactResult === "success" || compactResult === "failed") {
            this.compacting = false;
            events.push({
              type: "timeline",
              item: { type: "compaction", status: "completed" },
              provider: "claude",
            });
          }
        } else if (message.subtype === "compact_boundary") {
          const compactMetadata = readCompactionMetadata(message as Record<string, unknown>);
          events.push({
            type: "timeline",
            item: {
              type: "compaction",
              status: "completed",
              trigger: compactMetadata?.trigger === "manual" ? "manual" : "auto",
              preTokens: compactMetadata?.preTokens,
            },
            provider: "claude",
          });
        } else if (message.subtype === "task_notification") {
          const taskNotificationItem = mapTaskNotificationSystemRecordToToolCall(message);
          if (taskNotificationItem) {
            events.push({
              type: "timeline",
              item: taskNotificationItem,
              provider: "claude",
            });
          }
          // NOTE: `task_notification.usage` describes the Task sub-agent's own
          // token budget, NOT the parent agent's context-window fill. Do not
          // route it into the main meter — doing so caused the meter to flip
          // to sub-agent totals whenever a Task tool emitted a notification.
        } else if (message.subtype === "task_progress") {
          // Same caveat as task_notification: `task_progress.usage.total_tokens`
          // is the Task sub-agent's running total, not the parent context fill.
          // The main meter is driven by stream `message_start` / `message_delta`
          // events (see `trackStreamEventUsage`) and the per-turn `result.usage`
          // fallback in `convertUsage`.
        }
        break;
      case "user": {
        if (isSyntheticUserEntry(message)) {
          break;
        }
        if (this.compacting) {
          this.compacting = false;
          break;
        }
        const messageId =
          typeof message.uuid === "string" && message.uuid.length > 0 ? message.uuid : undefined;
        this.rememberUserMessageId(messageId);
        const content = message.message?.content;
        const taskNotificationItem = mapTaskNotificationUserContentToToolCall({
          content,
          messageId,
        });
        if (taskNotificationItem) {
          events.push({
            type: "timeline",
            item: taskNotificationItem,
            provider: "claude",
          });
          break;
        }
        if (typeof content === "string" && content.length > 0) {
          if (!isClaudeTranscriptNoiseText(content)) {
            events.push({
              type: "timeline",
              item: {
                type: "user_message",
                text: content,
                ...(messageId ? { messageId } : {}),
              },
              provider: "claude",
            });
          }
        } else if (Array.isArray(content)) {
          const timelineItems = this.mapBlocksToTimeline(content, {
            textMessageType: "user_message",
          });
          for (const item of timelineItems) {
            if (item.type === "user_message" && messageId && !item.messageId) {
              events.push({
                type: "timeline",
                item: { ...item, messageId },
                provider: "claude",
              });
              continue;
            }
            events.push({ type: "timeline", item, provider: "claude" });
          }
        }
        break;
      }
      case "assistant": {
        const timelineItems = this.mapBlocksToTimeline(message.message.content, {
          suppressAssistantText: options?.suppressAssistantText ?? false,
          suppressReasoning: options?.suppressReasoning ?? false,
        });
        for (const item of timelineItems) {
          events.push({ type: "timeline", item, provider: "claude" });
        }
        break;
      }
      case "stream_event": {
        const usageUpdatedEvent = this.trackStreamEventUsage(message.event);
        if (usageUpdatedEvent) {
          events.push(usageUpdatedEvent);
        }
        const timelineItems = this.mapPartialEvent(message.event, {
          suppressAssistantText: options?.suppressAssistantText ?? false,
          suppressReasoning: options?.suppressReasoning ?? false,
        });
        for (const item of timelineItems) {
          events.push({ type: "timeline", item, provider: "claude" });
        }
        break;
      }
      case "result": {
        const usage = this.convertUsage(message, message.modelUsage);
        if (message.subtype === "success") {
          events.push({ type: "turn_completed", provider: "claude", usage });
        } else {
          const errorMessage =
            "errors" in message && Array.isArray(message.errors) && message.errors.length > 0
              ? message.errors.join("\n")
              : "Claude run failed";
          events.push(this.buildTurnFailedEvent(errorMessage));
        }
        break;
      }
      case "rate_limit_event": {
        // SDK 0.2.x ships `SDKRateLimitInfo` with `rateLimitType`/`utilization`
        // as OPTIONAL — only `status` is required. We log the raw payload so we
        // can see what the SDK actually emits in the wild (different
        // subscription tiers / states ship different field combinations), then
        // only commit an entry when BOTH a known `rateLimitType` AND a numeric
        // `utilization` are present. Defaulting utilization to 0 was lying —
        // it produced "0% resets in 5h" tooltips on partial payloads.
        const info = (message as unknown as { rate_limit_info?: unknown }).rate_limit_info;
        this.logger.info(
          {
            module: "claude-agent",
            event: "rate_limit_event",
            rateLimitInfo: info,
          },
          "rate_limit_event received from Claude SDK",
        );
        if (info && typeof info === "object") {
          const { rateLimitType, utilization, resetsAt } = info as Record<string, unknown>;
          const VALID_TYPES = new Set<string>([
            "five_hour",
            "seven_day",
            "seven_day_opus",
            "seven_day_sonnet",
            "overage",
          ]);
          let mutated = false;
          if (
            typeof rateLimitType === "string" &&
            VALID_TYPES.has(rateLimitType) &&
            typeof utilization === "number" &&
            Number.isFinite(utilization)
          ) {
            const rateLimit: AgentRateLimit = {
              rateLimitType: rateLimitType as RateLimitType,
              utilization,
            };
            if (typeof resetsAt === "number" && Number.isFinite(resetsAt)) {
              rateLimit.resetsAt = resetsAt;
            }
            const existingLimits = this.lastRateLimits ?? [];
            const updated = existingLimits.filter((l) => l.rateLimitType !== rateLimitType);
            updated.push(rateLimit);
            this.lastRateLimits = updated;
            mutated = true;
          }
          // Only emit `usage_updated` when we actually have new rate-limit data
          // to publish. Spamming empty updates would only clear the meter for
          // no reason.
          if (mutated && this.lastRateLimits !== undefined) {
            const usage: AgentUsage = { rateLimits: this.lastRateLimits };
            if (this.lastContextWindowMaxTokens !== undefined) {
              usage.contextWindowMaxTokens = this.lastContextWindowMaxTokens;
            }
            if (this.lastContextWindowUsedTokens !== undefined) {
              usage.contextWindowUsedTokens = this.lastContextWindowUsedTokens;
            }
            events.push({ type: "usage_updated", provider: "claude", usage });
          }
        }
        break;
      }
      default:
        break;
    }

    return events;
  }

  private captureSessionIdFromMessage(message: SDKMessage): string | null {
    const msg = message as unknown as {
      session_id?: unknown;
      sessionId?: unknown;
      session?: { id?: unknown } | null;
    };
    const sessionIdRaw =
      typeof msg.session_id === "string"
        ? msg.session_id
        : typeof msg.sessionId === "string"
          ? msg.sessionId
          : typeof msg.session?.id === "string"
            ? msg.session.id
            : "";
    const sessionId = sessionIdRaw.trim();
    if (!sessionId) {
      return null;
    }
    if (this.claudeSessionId === null) {
      this.claudeSessionId = sessionId;
      this.persistence = null;
      return sessionId;
    }
    if (this.claudeSessionId === sessionId) {
      return null;
    }
    // Session ID changed mid-stream (e.g. a hook caused Claude to restart
    // with a new session). Accept the new ID and continue — the turn should
    // not be failed just because the underlying subprocess cycled.
    this.logger.warn(
      { existingSessionId: this.claudeSessionId, newSessionId: sessionId },
      "Claude session ID changed in message; accepting new session",
    );
    this.claudeSessionId = sessionId;
    this.persistence = null;
    return sessionId;
  }

  private handleSystemMessage(message: SDKSystemMessage): string | null {
    if (message.subtype !== "init") {
      return null;
    }

    const msg = message as unknown as {
      session_id?: unknown;
      sessionId?: unknown;
      session?: { id?: unknown } | null;
    };
    const newSessionIdRaw =
      typeof msg.session_id === "string"
        ? msg.session_id
        : typeof msg.sessionId === "string"
          ? msg.sessionId
          : typeof msg.session?.id === "string"
            ? msg.session.id
            : "";
    const newSessionId = newSessionIdRaw.trim();
    if (!newSessionId) {
      return null;
    }
    const existingSessionId = this.claudeSessionId;
    let threadStartedSessionId: string | null = null;

    if (existingSessionId === null) {
      this.claudeSessionId = newSessionId;
      threadStartedSessionId = newSessionId;
      this.logger.debug({ sessionId: newSessionId }, "Claude session ID set for the first time");
    } else if (existingSessionId === newSessionId) {
      this.logger.debug({ sessionId: newSessionId }, "Claude session ID unchanged (same value)");
    } else {
      // Session ID changed in an init message (e.g. a hook restarted Claude
      // with a new session mid-turn). Accept the new ID and continue.
      this.logger.warn(
        { existingSessionId, newSessionId },
        "Claude session ID changed in init message; accepting new session",
      );
      this.claudeSessionId = newSessionId;
      threadStartedSessionId = newSessionId;
    }
    this.availableModes = DEFAULT_MODES;
    this.currentMode = message.permissionMode;
    if (this.currentMode !== "plan") {
      this.planResumeMode = this.currentMode;
    }
    this.persistence = null;
    if (message.model) {
      const normalizedRuntimeModel = normalizeClaudeRuntimeModelId(message.model);
      this.logger.debug(
        { runtimeModel: message.model, normalizedRuntimeModel },
        "Captured runtime model from SDK init",
      );
      if (normalizedRuntimeModel) {
        this.lastOptionsModel = normalizedRuntimeModel;
      } else if (!this.lastOptionsModel) {
        this.lastOptionsModel = this.config.model ?? null;
      }
      this.lastRuntimeModel = message.model;
      this.cachedRuntimeInfo = null;
    }
    return threadStartedSessionId;
  }

  private readMissingResumedConversationError(message: SDKMessage): string | null {
    if (message.type !== "result" || message.subtype !== "error_during_execution") {
      return null;
    }
    if (!this.claudeSessionId) {
      return null;
    }
    const errors = "errors" in message && Array.isArray(message.errors) ? message.errors : [];
    for (const entry of errors) {
      if (typeof entry !== "string") {
        continue;
      }
      const match = entry.match(/^No conversation found with session ID:\s*(.+)$/);
      if (!match) {
        continue;
      }
      if (match[1]?.trim() === this.claudeSessionId) {
        return entry.trim();
      }
    }
    return null;
  }

  private convertUsage(message: SDKResultMessage, modelUsage?: unknown): AgentUsage | undefined {
    if (!message.usage) {
      return undefined;
    }
    const usage: AgentUsage = {
      inputTokens: message.usage.input_tokens,
      cachedInputTokens: message.usage.cache_read_input_tokens,
      outputTokens: message.usage.output_tokens,
      totalCostUsd: message.total_cost_usd,
    };
    if (this.lastRateLimits !== undefined) {
      usage.rateLimits = this.lastRateLimits;
    }
    const contextWindowMaxTokens = extractContextWindowSize(modelUsage ?? message.modelUsage);
    if (contextWindowMaxTokens !== undefined) {
      this.lastContextWindowMaxTokens = contextWindowMaxTokens;
      usage.contextWindowMaxTokens = contextWindowMaxTokens;
    } else if (this.lastContextWindowMaxTokens !== undefined) {
      usage.contextWindowMaxTokens = this.lastContextWindowMaxTokens;
    }
    if (
      typeof this.lastStreamRequestInputTokens === "number" &&
      typeof this.lastStreamRequestOutputTokens === "number"
    ) {
      // Stream `message_start` carries the real per-turn request size
      // (input + cache_creation + cache_read); `message_delta` then accumulates
      // output_tokens. This is the most accurate live signal for the meter.
      usage.contextWindowUsedTokens =
        this.lastStreamRequestInputTokens + this.lastStreamRequestOutputTokens;
    } else if (typeof this.lastContextWindowUsedTokens === "number") {
      // Legacy fallback. As of the task_progress / task_notification fix, no
      // path writes this field anymore; keep the read for forward-compat.
      usage.contextWindowUsedTokens = this.lastContextWindowUsedTokens;
    } else if (message.usage) {
      // Fallback: derive from result.usage when no task_progress has been
      // received yet. These values are accumulated across all API calls, but
      // for the first turn they equal the per-call values so the estimate is
      // reasonable. Once a task_progress arrives it takes over permanently.
      const usageWithCacheCreation = message.usage as typeof message.usage & {
        cache_creation_input_tokens?: number;
      };
      const derived =
        (message.usage.input_tokens ?? 0) +
        (usageWithCacheCreation.cache_creation_input_tokens ?? 0) +
        (message.usage.cache_read_input_tokens ?? 0) +
        (message.usage.output_tokens ?? 0);
      if (Number.isFinite(derived) && derived > 0) {
        usage.contextWindowUsedTokens = derived;
      }
    }
    return usage;
  }

  private createUsageUpdatedEvent(contextWindowUsedTokens: number): AgentStreamEvent {
    const usage: AgentUsage = {
      contextWindowUsedTokens,
    };
    if (this.lastContextWindowMaxTokens !== undefined) {
      usage.contextWindowMaxTokens = this.lastContextWindowMaxTokens;
    }
    return {
      type: "usage_updated",
      provider: "claude",
      usage,
    };
  }

  private trackStreamEventUsage(event: unknown): AgentStreamEvent | null {
    if (!event || typeof event !== "object") {
      return null;
    }
    const streamEvent = event as Record<string, unknown>;
    const eventType = readTrimmedString(streamEvent.type);
    if (eventType === "message_start") {
      const inputTokens = readStreamRequestInputTokens(streamEvent);
      if (typeof inputTokens !== "number") {
        return null;
      }
      this.lastStreamRequestInputTokens = inputTokens;
      this.lastStreamRequestOutputTokens = 0;
    } else if (eventType === "message_delta") {
      const outputTokens = readStreamRequestOutputTokens(streamEvent);
      if (typeof outputTokens !== "number") {
        return null;
      }
      this.lastStreamRequestOutputTokens = outputTokens;
    } else {
      return null;
    }

    if (
      typeof this.lastStreamRequestInputTokens !== "number" ||
      typeof this.lastStreamRequestOutputTokens !== "number"
    ) {
      return null;
    }
    return this.createUsageUpdatedEvent(
      this.lastStreamRequestInputTokens + this.lastStreamRequestOutputTokens,
    );
  }

  private maybeRewritePlanFilename(toolName: string, input: Record<string, unknown>): void {
    try {
      if (toolName !== "Write") {
        return;
      }
      const filePath = input.file_path;
      const content = input.content;
      if (typeof filePath !== "string" || typeof content !== "string") {
        return;
      }
      const dir = path.dirname(filePath);
      const dirName = path.basename(dir);
      const baseName = path.basename(filePath);
      if (dirName !== ".plans" || !baseName.endsWith(".md")) {
        return;
      }
      const name = extractPlanNameFromFrontmatter(content);
      if (!name) {
        return;
      }
      const slug = slugifyPlanName(name);
      if (slug.length === 0) {
        return;
      }
      const currentStem = baseName.slice(0, -".md".length);
      if (currentStem === slug) {
        return;
      }
      const newPath = resolvePlanFilename({
        originalPath: filePath,
        newSlug: slug,
        existsSync: fs.existsSync,
      });
      if (newPath === filePath) {
        return;
      }
      input.file_path = newPath;
      this.logger.info(
        {
          source: "plan-filename-auto-rename",
          before: filePath,
          after: newPath,
        },
        "Rewrote plan filename from frontmatter name",
      );
    } catch (error) {
      this.logger.warn(
        { err: error, source: "plan-filename-auto-rename" },
        "Failed to rewrite plan filename; passing through original path",
      );
    }
  }

  private handlePermissionRequest: CanUseTool = async (
    toolName,
    input,
    options,
  ): Promise<PermissionResult> => {
    this.maybeRewritePlanFilename(toolName, input);
    if (this.currentMode === "plan" && toolName === "Write") {
      const filePath = typeof input.file_path === "string" ? input.file_path : "";
      const dirName = path.basename(path.dirname(filePath));
      if (dirName === ".plans" && filePath.endsWith(".md")) {
        return {
          behavior: "deny",
          message:
            "In plan mode, plans must be created via `mcp__appostle__write_plan` (typed schema, validated frontmatter). Call that tool instead of raw Write.",
          interrupt: false,
        };
      }
    }
    const requestId = `permission-${randomUUID()}`;
    const kind = resolvePermissionKind(toolName, input);
    // Honor the active permission mode locally. The SDK calls canUseTool for every
    // tool when one is supplied, regardless of permissionMode — so the daemon is
    // responsible for short-circuiting bypassPermissions / acceptEdits itself.
    // Plan and question kinds always go through the client UI (ExitPlanMode /
    // AskUserQuestion have dedicated flows that depend on the permission event).
    if (kind === "tool") {
      if (this.currentMode === "bypassPermissions") {
        return { behavior: "allow", updatedInput: input };
      }
      if (this.currentMode === "acceptEdits" && CLAUDE_ACCEPT_EDITS_AUTO_ALLOWED.has(toolName)) {
        return { behavior: "allow", updatedInput: input };
      }
    }
    const metadata: AgentMetadata = {};
    if (options.toolUseID) {
      metadata.toolUseId = options.toolUseID;
    }
    if (toolName === "ExitPlanMode" && typeof input.plan === "string") {
      metadata.planText = input.plan;
    }
    const toolDetail =
      kind === "tool"
        ? mapClaudeRunningToolCall({
            name: toolName,
            callId: options.toolUseID ?? requestId,
            input,
            output: null,
          })?.detail
        : undefined;

    const request: AgentPermissionRequest = {
      id: requestId,
      provider: "claude",
      name: toolName,
      kind,
      input,
      detail: toolDetail,
      suggestions: options.suggestions?.map((suggestion) => ({ ...suggestion })),
      actions: kind === "plan" ? buildClaudePlanPermissionActions(this.planResumeMode) : undefined,
      metadata: Object.keys(metadata).length ? metadata : undefined,
    };

    this.pushEvent({ type: "permission_requested", provider: "claude", request });

    return await new Promise<PermissionResult>((resolve, reject) => {
      const cleanupFns: Array<() => void> = [];
      const cleanup = () => {
        while (cleanupFns.length) {
          const fn = cleanupFns.pop();
          try {
            fn?.();
          } catch {
            // ignore cleanup errors
          }
        }
      };

      const abortHandler = () => {
        this.pendingPermissions.delete(requestId);
        cleanup();
        reject(new Error("Permission request aborted"));
      };

      if (options?.signal) {
        if (options.signal.aborted) {
          abortHandler();
          return;
        }
        options.signal.addEventListener("abort", abortHandler, { once: true });
        cleanupFns.push(() => options.signal?.removeEventListener("abort", abortHandler));
      }

      this.pendingPermissions.set(requestId, {
        request,
        resolve,
        reject,
        cleanup,
      });
    });
  };

  private enqueueTimeline(item: AgentTimelineItem) {
    this.pushEvent({ type: "timeline", item, provider: "claude" });
  }

  private flushPendingToolCalls() {
    for (const [id, entry] of this.toolUseCache) {
      if (entry.started) {
        this.pushToolCall(
          mapClaudeCanceledToolCall({
            name: entry.name,
            callId: id,
            input: entry.input ?? null,
            output: null,
          }),
        );
      }
    }
    // Emit a terminating chunk for every file we were streaming so clients
    // unfreeze their "writing…" state even when a turn is canceled.
    for (const toolUseId of this.fileChunkFilePaths.keys()) {
      this.finalizeFileChunkStream(toolUseId);
    }
    this.toolUseCache.clear();
    this.toolUseInputBuffers.clear();
    this.fileChunkSequences.clear();
    this.fileChunkFilePaths.clear();
    this.fileChunkLastContent.clear();
    this.sidechainTracker.clear();
  }

  private pushToolCall(
    item: Extract<AgentTimelineItem, { type: "tool_call" }> | null,
    target?: AgentTimelineItem[],
  ) {
    if (!item) {
      return;
    }
    if (target) {
      target.push(item);
      return;
    }
    this.enqueueTimeline(item);
  }

  private pushEvent(event: AgentStreamEvent) {
    this.notifySubscribers(event);
  }

  private notifySubscribers(event: AgentStreamEvent): void {
    const turnId = this.activeForegroundTurnId ?? this.autonomousTurn?.id;
    const tagged = turnId ? { ...event, turnId } : event;
    for (const callback of this.subscribers) {
      try {
        callback(tagged);
      } catch (error) {
        this.logger.warn({ err: error }, "Subscriber callback threw");
      }
    }
  }

  private normalizePermissionUpdates(
    updates?: AgentPermissionUpdate[],
  ): PermissionUpdate[] | undefined {
    if (!updates || updates.length === 0) {
      return undefined;
    }
    const normalized = updates.filter(isPermissionUpdate);
    return normalized.length > 0 ? normalized : undefined;
  }

  private rejectAllPendingPermissions(error: Error) {
    for (const [id, pending] of this.pendingPermissions) {
      pending.cleanup?.();
      pending.reject(error);
      this.pendingPermissions.delete(id);
    }
  }

  private loadPersistedHistory(sessionId: string): void {
    try {
      const historyPath = this.resolveHistoryPath(sessionId);
      if (!historyPath || !fs.existsSync(historyPath)) {
        return;
      }
      this.ingestPersistedHistory(fs.readFileSync(historyPath, "utf8"));
    } catch (error) {
      // ignore history load failures
    }
  }

  private ingestPersistedHistory(content: string): void {
    if (!content) {
      return;
    }

    const timeline: AgentTimelineItem[] = [];
    for (const line of content.split(/\r?\n/)) {
      this.ingestPersistedHistoryLine(line, timeline);
    }

    if (timeline.length > 0) {
      this.persistedHistory = [...this.persistedHistory, ...timeline];
      this.historyPending = true;
    }
  }

  private ingestPersistedHistoryLine(line: string, timeline: AgentTimelineItem[]): void {
    const trimmed = line.trim();
    if (!trimmed) {
      return;
    }

    let entry: Record<string, unknown>;
    try {
      entry = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      return;
    }

    if (entry.isSidechain) {
      return;
    }
    if (entry.type === "user" && typeof entry.uuid === "string") {
      this.rememberUserMessageId(entry.uuid);
    }

    const items = this.convertHistoryEntry(entry);
    if (items.length > 0) {
      timeline.push(...items);
    }
  }

  private resolveHistoryPath(sessionId: string): string | null {
    const cwd = this.config.cwd;
    if (!cwd) return null;
    const sanitized = sanitizeClaudeProjectPath(cwd);
    const configDir = process.env.CLAUDE_CONFIG_DIR ?? path.join(os.homedir(), ".claude");
    const dir = path.join(configDir, "projects", sanitized);
    return path.join(dir, `${sessionId}.jsonl`);
  }

  private convertHistoryEntry(entry: any): AgentTimelineItem[] {
    return convertClaudeHistoryEntry(entry, (content) => this.mapBlocksToTimeline(content));
  }

  // Maps Claude content blocks into AgentTimelineItems.
  //
  // textMessageType controls what type text blocks emit:
  //   - "assistant_message" (default): one item per text block (streaming granularity)
  //   - "user_message": coalesces all text blocks into a single user_message
  //     (matches extractUserMessageText semantics: trim each block, join with "\n\n")
  //
  // suppressAssistantText only applies when textMessageType is "assistant_message" — user text
  // must never be suppressed since the TimelineAssembler only handles assistant text.
  //
  // NOTE: convertClaudeHistoryEntry uses extractUserMessageText directly instead of this function
  // for user entries. Both paths must produce equivalent user_message items.
  private mapBlocksToTimeline(
    content: string | ClaudeContentChunk[],
    options?: {
      textMessageType?: "assistant_message" | "user_message";
      suppressAssistantText?: boolean;
      suppressReasoning?: boolean;
    },
  ): AgentTimelineItem[] {
    const textMessageType = options?.textMessageType ?? "assistant_message";
    const suppressText =
      textMessageType === "assistant_message" && (options?.suppressAssistantText ?? false);
    const suppressReasoning = options?.suppressReasoning ?? false;

    if (typeof content === "string") {
      if (
        !content ||
        content === INTERRUPT_TOOL_USE_PLACEHOLDER ||
        isClaudeTranscriptNoiseText(content)
      ) {
        return [];
      }
      if (suppressText) {
        return [];
      }
      return [{ type: textMessageType, text: content }];
    }

    const items: AgentTimelineItem[] = [];
    // User SDK entries can arrive as multiple text blocks, but Appostle treats them as one message.
    const userTextParts: string[] = [];
    for (const block of content) {
      switch (block.type) {
        case "text":
        case "text_delta":
          if (
            block.text &&
            block.text !== INTERRUPT_TOOL_USE_PLACEHOLDER &&
            !isClaudeTranscriptNoiseText(block.text)
          ) {
            if (textMessageType === "user_message") {
              const trimmed = block.text.trim();
              if (trimmed) {
                userTextParts.push(trimmed);
              }
            } else if (!suppressText) {
              items.push({ type: "assistant_message", text: block.text });
            }
          }
          break;
        case "thinking":
        case "thinking_delta":
          if (block.thinking) {
            if (!suppressReasoning) {
              items.push({ type: "reasoning", text: block.thinking });
            }
          }
          break;
        case "tool_use":
        case "server_tool_use":
        case "mcp_tool_use": {
          this.handleToolUseStart(block, items);
          break;
        }
        case "tool_result":
        case "mcp_tool_result":
        case "web_fetch_tool_result":
        case "web_search_tool_result":
        case "code_execution_tool_result":
        case "bash_code_execution_tool_result":
        case "text_editor_code_execution_tool_result": {
          this.handleToolResult(block, items);
          break;
        }
        default:
          break;
      }
    }

    if (textMessageType === "user_message" && userTextParts.length > 0) {
      items.unshift({
        type: "user_message",
        text: userTextParts.join("\n\n"),
      });
    }

    return items;
  }

  private handleToolUseStart(block: ClaudeContentChunk, items: AgentTimelineItem[]): void {
    const entry = this.upsertToolUseEntry(block);
    if (!entry) {
      return;
    }
    if (entry.started) {
      return;
    }
    entry.started = true;
    this.toolUseCache.set(entry.id, entry);
    this.pushToolCall(
      mapClaudeRunningToolCall({
        name: entry.name,
        callId: entry.id,
        input: entry.input ?? this.normalizeToolInput(block.input) ?? null,
        output: null,
      }),
      items,
    );
  }

  private handleToolResult(block: ClaudeContentChunk, items: AgentTimelineItem[]): void {
    const entry =
      typeof block.tool_use_id === "string" ? this.toolUseCache.get(block.tool_use_id) : undefined;
    const toolName = entry?.name ?? block.tool_name ?? "tool";
    const callId =
      typeof block.tool_use_id === "string" && block.tool_use_id.length > 0
        ? block.tool_use_id
        : (entry?.id ?? null);

    // Extract output from block.content (SDK always returns content in string form)
    const output = this.buildToolOutput(block, entry);

    if (block.is_error) {
      this.pushToolCall(
        mapClaudeFailedToolCall({
          name: toolName,
          callId,
          input: entry?.input ?? null,
          output: output ?? null,
          error: block,
        }),
        items,
      );
    } else {
      this.pushToolCall(
        mapClaudeCompletedToolCall({
          name: toolName,
          callId,
          input: entry?.input ?? null,
          output: output ?? null,
        }),
        items,
      );
    }

    if (typeof block.tool_use_id === "string") {
      this.toolUseCache.delete(block.tool_use_id);
      this.sidechainTracker.delete(block.tool_use_id);
    }
  }

  private buildToolOutput(
    block: ClaudeContentChunk,
    entry: ToolUseCacheEntry | undefined,
  ): AgentMetadata | undefined {
    if (block.is_error) {
      return undefined;
    }

    // Inline image content blocks always win — before any name-based structured
    // handler can stringify them into a base64 text wall. Hand the raw image
    // blocks downstream so `extractImageToolDetail` promotes them to an image
    // detail (Read on a .png, MCP screenshot tools, etc.).
    const imageBlocks = extractImageContentBlocks(block.content);
    if (imageBlocks) {
      return { output: imageBlocks };
    }

    const server = entry?.server ?? block.server ?? "tool";
    const tool = entry?.name ?? block.tool_name ?? "tool";
    const content = coerceToolResultContentToString(block.content);
    const input = entry?.input;

    // Build structured result based on tool type
    const structured = this.buildStructuredToolResult(server, tool, content, input);

    if (structured) {
      return structured;
    }

    // Fallback format - try to parse JSON first
    const result: AgentMetadata = {};

    if (content.length > 0) {
      try {
        // If content is a JSON string, parse it
        result.output = JSON.parse(content);
      } catch {
        // If not JSON, return unchanged (no extra wrapping)
        result.output = content;
      }
    }

    // Preserve file changes tracked during tool execution
    if (entry?.files?.length) {
      result.files = entry.files;
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }

  private buildStructuredToolResult(
    server: string,
    tool: string,
    output: string,
    input?: AgentMetadata | null,
  ): AgentMetadata | undefined {
    const normalizedServer = server.toLowerCase();
    const normalizedTool = tool.toLowerCase();

    // Playwright MCP screenshot tools — Playwright returns a markdown text
    // pointer to a file it wrote to disk rather than inlining base64 image
    // blocks. Resolve the file from the agent's cwd, base64-encode it, and
    // hand downstream parsers an Anthropic-style image content array so
    // `extractImageToolDetail` promotes it to an `image` detail (mirroring
    // claude-in-chrome's behavior — inline preview + break the tool-call
    // group).
    if (
      normalizedTool === "mcp__playwright__browser_take_screenshot" ||
      normalizedTool === "browser_take_screenshot"
    ) {
      const imageOutput = this.tryInlinePlaywrightScreenshot(output, input);
      if (imageOutput) {
        return { output: imageOutput };
      }
    }

    // Command execution tools
    if (
      normalizedServer.includes("bash") ||
      normalizedServer.includes("shell") ||
      normalizedServer.includes("command") ||
      normalizedTool.includes("bash") ||
      normalizedTool.includes("shell") ||
      normalizedTool.includes("command") ||
      (input && (typeof input.command === "string" || Array.isArray(input.command)))
    ) {
      const command = this.extractCommandText(input ?? {}) ?? "command";
      return {
        type: "command",
        command,
        output,
        cwd: typeof input?.cwd === "string" ? input.cwd : undefined,
      };
    }

    // File write tools (new files or complete replacements)
    if (
      normalizedTool.includes("write") ||
      normalizedTool === "write_file" ||
      normalizedTool === "create_file"
    ) {
      if (input && typeof input.file_path === "string") {
        return {
          type: "file_write",
          filePath: input.file_path,
          oldContent: "",
          newContent: typeof input.content === "string" ? input.content : output,
        };
      }
    }

    // File edit/patch tools
    if (
      normalizedTool.includes("edit") ||
      normalizedTool.includes("patch") ||
      normalizedTool === "apply_patch" ||
      normalizedTool === "apply_diff"
    ) {
      if (input && typeof input.file_path === "string") {
        // Support both old_str/new_str and old_string/new_string parameter names
        const oldContent =
          typeof input.old_str === "string"
            ? input.old_str
            : typeof input.old_string === "string"
              ? input.old_string
              : undefined;
        const newContent =
          typeof input.new_str === "string"
            ? input.new_str
            : typeof input.new_string === "string"
              ? input.new_string
              : undefined;
        return {
          type: "file_edit",
          filePath: input.file_path,
          diff:
            typeof input.patch === "string"
              ? input.patch
              : typeof input.diff === "string"
                ? input.diff
                : undefined,
          oldContent,
          newContent,
        };
      }
    }

    // File read tools
    if (
      normalizedTool.includes("read") ||
      normalizedTool === "read_file" ||
      normalizedTool === "view_file"
    ) {
      if (input && typeof input.file_path === "string") {
        return {
          type: "file_read",
          filePath: input.file_path,
          content: output,
        };
      }
    }

    // Playwright MCP screenshot — the server returns a markdown link to a local
    // file instead of inline base64 image blocks.  Read the file from disk so
    // the client can render it as an inline image (same as Claude-in-Chrome).
    if (normalizedServer === "playwright" && normalizedTool === "browser_take_screenshot") {
      const imageDetail = this.tryReadPlaywrightScreenshot(output, input);
      if (imageDetail) return imageDetail;
    }

    return undefined;
  }

  /**
   * Playwright MCP's `browser_take_screenshot` saves the image to a local file
   * and returns a markdown link (`[Screenshot of viewport](./file.png)`).
   * Extract the path, resolve it against the agent CWD, read the bytes, and
   * return an image ToolCallDetail so the client renders it inline.
   */
  private tryReadPlaywrightScreenshot(
    output: string,
    input?: AgentMetadata | null,
  ): AgentMetadata | undefined {
    // The filename can come from the input (explicit) or the markdown output.
    let filePath: string | undefined;
    if (input && typeof input.filename === "string") {
      filePath = input.filename;
    }
    if (!filePath) {
      const match = output.match(/\[Screenshot[^\]]*\]\(([^)]+)\)/i);
      if (match?.[1]) {
        filePath = match[1];
      }
    }
    if (!filePath) return undefined;

    const cwd = this.config.cwd;
    if (!cwd) return undefined;

    const abs = path.isAbsolute(filePath) ? filePath : path.resolve(cwd, filePath);
    try {
      const buf = fs.readFileSync(abs);
      const ext = path.extname(abs).toLowerCase();
      const mime = ext === ".jpeg" || ext === ".jpg" ? "image/jpeg" : "image/png";
      return {
        type: "image",
        toolName: "mcp__playwright__browser_take_screenshot",
        images: [{ mimeType: mime, data: buf.toString("base64") }],
      };
    } catch {
      return undefined;
    }
  }

  private mapPartialEvent(
    event: SDKPartialAssistantMessage["event"],
    options?: {
      suppressAssistantText?: boolean;
      suppressReasoning?: boolean;
    },
  ): AgentTimelineItem[] {
    if (event.type === "content_block_start") {
      const block = isClaudeContentChunk(event.content_block) ? event.content_block : null;
      if (
        block?.type === "tool_use" &&
        typeof event.index === "number" &&
        typeof block.id === "string"
      ) {
        this.toolUseIndexToId.set(event.index, block.id);
        this.toolUseInputBuffers.delete(block.id);
      }
    } else if (event.type === "content_block_delta") {
      const delta = isClaudeContentChunk(event.delta) ? event.delta : null;
      if (delta?.type === "input_json_delta") {
        const partialJson = typeof delta.partial_json === "string" ? delta.partial_json : undefined;
        this.handleToolInputDelta(event.index, partialJson);
        return [];
      }
    } else if (event.type === "content_block_stop" && typeof event.index === "number") {
      const toolId = this.toolUseIndexToId.get(event.index);
      if (toolId) {
        this.finalizeFileChunkStream(toolId);
        this.toolUseIndexToId.delete(event.index);
        this.toolUseInputBuffers.delete(toolId);
      }
    }

    switch (event.type) {
      case "content_block_start":
        return isClaudeContentChunk(event.content_block)
          ? this.mapBlocksToTimeline([event.content_block], {
              suppressAssistantText: options?.suppressAssistantText,
              suppressReasoning: options?.suppressReasoning,
            })
          : [];
      case "content_block_delta":
        return isClaudeContentChunk(event.delta)
          ? this.mapBlocksToTimeline([event.delta], {
              suppressAssistantText: options?.suppressAssistantText,
              suppressReasoning: options?.suppressReasoning,
            })
          : [];
      default:
        return [];
    }
  }

  private upsertToolUseEntry(block: ClaudeContentChunk): ToolUseCacheEntry | null {
    const id = typeof block.id === "string" ? block.id : undefined;
    if (!id) {
      return null;
    }
    const existing =
      this.toolUseCache.get(id) ??
      ({
        id,
        name: typeof block.name === "string" && block.name.length > 0 ? block.name : "tool",
        server:
          typeof block.server === "string" && block.server.length > 0
            ? block.server
            : typeof block.name === "string" && block.name.length > 0
              ? block.name
              : "tool",
        classification: "generic",
        started: false,
      } satisfies ToolUseCacheEntry);

    if (typeof block.name === "string" && block.name.length > 0) {
      existing.name = block.name;
    }
    if (typeof block.server === "string" && block.server.length > 0) {
      existing.server = block.server;
    } else if (!existing.server) {
      existing.server = existing.name;
    }

    if (
      block.type === "tool_use" ||
      block.type === "mcp_tool_use" ||
      block.type === "server_tool_use"
    ) {
      const input = this.normalizeToolInput(block.input);
      if (input) {
        this.applyToolInput(existing, input);
      }
    }

    this.toolUseCache.set(id, existing);
    return existing;
  }

  private handleToolInputDelta(index: number | undefined, partialJson: string | undefined): void {
    if (typeof index !== "number" || typeof partialJson !== "string") {
      return;
    }
    const toolId = this.toolUseIndexToId.get(index);
    if (!toolId) {
      return;
    }
    const buffer = (this.toolUseInputBuffers.get(toolId) ?? "") + partialJson;
    this.toolUseInputBuffers.set(toolId, buffer);
    const entry = this.toolUseCache.get(toolId);

    // Character-level streaming: extract the Write tool's content field
    // directly from the partial JSON buffer, without waiting for a full
    // parse. Emits a file_chunk every time the extracted content grows
    // so the editor sees tokens land character-by-character (VS Code /
    // Cursor-style "typing" animation) instead of one lump at parse
    // completion. Safe to call on every delta — dedupes internally.
    if (entry) {
      this.emitProgressiveFileChunk(toolId, entry.name, buffer);
    }

    const parsed = parsePartialJsonObject(buffer);
    if (!entry || !parsed) {
      return;
    }
    const normalized = this.normalizeToolInput(parsed.value);
    if (!normalized) {
      return;
    }
    if (!parsed.complete && Object.keys(normalized).length === 0) {
      return;
    }
    if (this.areToolInputsEqual(entry.input ?? undefined, normalized)) {
      return;
    }
    this.applyToolInput(entry, normalized);
    this.toolUseCache.set(toolId, entry);
    this.pushToolCall(
      mapClaudeRunningToolCall({
        name: entry.name,
        callId: toolId,
        input: normalized,
        output: null,
      }),
    );
    // File-chunk streaming is now driven by emitProgressiveFileChunk() in
    // the delta handler, which emits per-character updates straight from
    // the raw partial_json buffer. The parse-based path would only re-fire
    // the same full-content snapshot at a higher sequence — skip it.
  }

  // Character-level streaming path. Called on every input_json_delta as
  // soon as partial JSON tokens arrive, BEFORE the parser has a complete
  // object. Extracts as much of the Write tool's content field as is
  // currently visible in the buffer, and emits a progressive chunk if
  // anything changed. This is what gives the editor its "typing" feel.
  //
  // Only handles write-family tools; Edit / MultiEdit are diff-shaped and
  // can't be reconstructed without reading the pre-edit file on disk.
  private emitProgressiveFileChunk(toolUseId: string, toolName: string, buffer: string): void {
    const normalizedName = toolName.toLowerCase();
    const isWriteTool =
      normalizedName.includes("write") ||
      normalizedName === "create_file" ||
      normalizedName === "write_file";
    if (!isWriteTool) {
      return;
    }
    const extracted = extractProgressiveWriteInput(buffer);
    if (!extracted || !extracted.filePath) {
      return;
    }
    const previous = this.fileChunkLastContent.get(toolUseId);
    if (previous !== undefined && previous === extracted.content) {
      return;
    }
    this.fileChunkLastContent.set(toolUseId, extracted.content);
    this.fileChunkFilePaths.set(toolUseId, extracted.filePath);
    const nextSeq = (this.fileChunkSequences.get(toolUseId) ?? -1) + 1;
    this.fileChunkSequences.set(toolUseId, nextSeq);
    this.pushEvent({
      type: "file_chunk",
      provider: this.provider,
      toolUseId,
      filePath: extracted.filePath,
      content: extracted.content,
      isComplete: false,
      sequenceId: nextSeq,
    });
  }

  // Called on content_block_stop for any tool_use we were streaming so
  // the client can settle the editor even when the final delta didn't
  // carry a state change worth re-emitting from the delta handler.
  private finalizeFileChunkStream(toolUseId: string): void {
    const filePath = this.fileChunkFilePaths.get(toolUseId);
    if (!filePath) {
      return;
    }
    const entry = this.toolUseCache.get(toolUseId);
    const content =
      entry && typeof (entry.input as AgentMetadata | undefined)?.content === "string"
        ? ((entry.input as AgentMetadata).content as string)
        : "";
    const nextSeq = (this.fileChunkSequences.get(toolUseId) ?? -1) + 1;
    this.fileChunkSequences.set(toolUseId, nextSeq);
    this.pushEvent({
      type: "file_chunk",
      provider: this.provider,
      toolUseId,
      filePath,
      content,
      isComplete: true,
      sequenceId: nextSeq,
    });
    this.fileChunkSequences.delete(toolUseId);
    this.fileChunkFilePaths.delete(toolUseId);
    this.fileChunkLastContent.delete(toolUseId);
  }

  private normalizeToolInput(input: unknown): AgentMetadata | null {
    if (!isMetadata(input)) {
      return null;
    }
    return input;
  }

  /**
   * Playwright MCP returns `### Result\n- [Screenshot of viewport](./foo.png)`
   * and writes the file to disk instead of inlining the image. Resolve that
   * path against the agent cwd and turn the file into an Anthropic-style
   * image content block array, so downstream `extractImageToolDetail`
   * promotes the tool call to a renderable `image` detail.
   *
   * Returns `null` when the path cannot be resolved/read safely; the caller
   * then falls back to the default text output behavior.
   */
  private tryInlinePlaywrightScreenshot(
    output: string,
    input?: AgentMetadata | null,
  ): Array<{ type: "image"; mimeType: string; data: string }> | null {
    const cwd = this.config.cwd;
    if (!cwd) return null;

    // Prefer the explicit `filename` field from the tool input — it survives
    // even when the markdown output is truncated by upstream wrappers.
    let relPath: string | undefined;
    if (input && typeof input.filename === "string" && input.filename.length > 0) {
      relPath = input.filename;
    } else {
      const match = output.match(/\[Screenshot[^\]]*\]\(([^)]+)\)/);
      if (match?.[1]) {
        relPath = match[1];
      }
    }
    if (!relPath) return null;

    const resolved = path.resolve(cwd, relPath);
    // Confine reads to the agent's cwd — Playwright should never be asked to
    // write outside it, and we don't want a crafted output to coerce us into
    // reading arbitrary files.
    const cwdReal = path.resolve(cwd);
    if (!resolved.startsWith(cwdReal + path.sep) && resolved !== cwdReal) {
      return null;
    }

    let buf: Buffer;
    try {
      buf = fs.readFileSync(resolved);
    } catch {
      return null;
    }

    const ext = path.extname(resolved).toLowerCase();
    const mimeType =
      ext === ".jpg" || ext === ".jpeg"
        ? "image/jpeg"
        : ext === ".webp"
          ? "image/webp"
          : ext === ".gif"
            ? "image/gif"
            : "image/png";

    return [{ type: "image", mimeType, data: buf.toString("base64") }];
  }

  private areToolInputsEqual(left: AgentMetadata | undefined, right: AgentMetadata): boolean {
    if (!left) {
      return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    return rightKeys.every((key) => left[key] === right[key]);
  }

  private applyToolInput(entry: ToolUseCacheEntry, input: AgentMetadata): void {
    entry.input = input;
    if (this.isCommandTool(entry.name, input)) {
      entry.classification = "command";
      entry.commandText = this.extractCommandText(input) ?? entry.commandText;
    } else {
      const files = this.extractFileChanges(input);
      if (files?.length) {
        entry.classification = "file_change";
        entry.files = files;
      }
    }
  }

  private isCommandTool(name: string, input: AgentMetadata): boolean {
    const normalized = name.toLowerCase();
    if (
      normalized.includes("bash") ||
      normalized.includes("shell") ||
      normalized.includes("terminal") ||
      normalized.includes("command")
    ) {
      return true;
    }
    if (typeof input.command === "string" || Array.isArray(input.command)) {
      return true;
    }
    return false;
  }

  private extractCommandText(input: AgentMetadata): string | undefined {
    const command = input.command;
    if (typeof command === "string" && command.length > 0) {
      return command;
    }
    if (Array.isArray(command)) {
      const tokens = command.filter((value): value is string => typeof value === "string");
      if (tokens.length > 0) {
        return tokens.join(" ");
      }
    }
    if (typeof input.description === "string" && input.description.length > 0) {
      return input.description;
    }
    return undefined;
  }

  private extractFileChanges(input: AgentMetadata): { path: string; kind: string }[] | undefined {
    if (typeof input.file_path === "string" && input.file_path.length > 0) {
      const relative = this.relativizePath(input.file_path);
      if (relative) {
        return [{ path: relative, kind: this.detectFileKind(input.file_path) }];
      }
    }
    if (typeof input.patch === "string" && input.patch.length > 0) {
      const files = this.parsePatchFileList(input.patch);
      if (files.length > 0) {
        return files.map((entry) => ({
          path: this.relativizePath(entry.path) ?? entry.path,
          kind: entry.kind,
        }));
      }
    }
    if (Array.isArray(input.files)) {
      const files: { path: string; kind: string }[] = [];
      for (const value of input.files) {
        if (typeof value === "string" && value.length > 0) {
          files.push({
            path: this.relativizePath(value) ?? value,
            kind: this.detectFileKind(value),
          });
        }
      }
      if (files.length > 0) {
        return files;
      }
    }
    return undefined;
  }

  private detectFileKind(filePath: string): string {
    try {
      return fs.existsSync(filePath) ? "update" : "add";
    } catch {
      return "update";
    }
  }

  private relativizePath(target?: string): string | undefined {
    if (!target) {
      return undefined;
    }
    const cwd = this.config.cwd;
    if (cwd && target.startsWith(cwd)) {
      const relative = path.relative(cwd, target);
      return relative.length > 0 ? relative : path.basename(target);
    }
    return target;
  }

  private parsePatchFileList(patch: string): { path: string; kind: string }[] {
    const files: { path: string; kind: string }[] = [];
    const seen = new Set<string>();
    for (const line of patch.split(/\r?\n/)) {
      const trimmed = line.trim();
      let kind: string | null = null;
      let parsedPath: string | null = null;
      if (trimmed.startsWith("*** Add File:")) {
        kind = "add";
        parsedPath = trimmed.replace("*** Add File:", "").trim();
      } else if (trimmed.startsWith("*** Delete File:")) {
        kind = "delete";
        parsedPath = trimmed.replace("*** Delete File:", "").trim();
      } else if (trimmed.startsWith("*** Update File:")) {
        kind = "update";
        parsedPath = trimmed.replace("*** Update File:", "").trim();
      }
      if (kind && parsedPath && !seen.has(`${kind}:${parsedPath}`)) {
        seen.add(`${kind}:${parsedPath}`);
        files.push({ path: parsedPath, kind });
      }
    }
    return files;
  }
}

function hasToolLikeBlock(block?: ClaudeContentChunk | null): boolean {
  if (!block || typeof block !== "object") {
    return false;
  }
  const type = typeof block.type === "string" ? block.type.toLowerCase() : "";
  return type.includes("tool");
}

function readCompactionMetadata(
  source: Record<string, unknown>,
): { trigger?: string; preTokens?: number } | null {
  const candidates = [source.compact_metadata, source.compactMetadata, source.compactionMetadata];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") {
      continue;
    }
    const metadata = candidate as Record<string, unknown>;
    const trigger = typeof metadata.trigger === "string" ? metadata.trigger : undefined;
    const preTokensRaw = metadata.preTokens ?? metadata.pre_tokens;
    const preTokens = typeof preTokensRaw === "number" ? preTokensRaw : undefined;
    return { trigger, preTokens };
  }
  return null;
}

function normalizeHistoryBlocks(content: unknown): ClaudeContentChunk[] | null {
  if (Array.isArray(content)) {
    const blocks = content.filter((entry) => isClaudeContentChunk(entry));
    return blocks.length > 0 ? blocks : null;
  }
  if (isClaudeContentChunk(content)) {
    return [content];
  }
  return null;
}

export function convertClaudeHistoryEntry(
  entry: any,
  mapBlocks: (content: string | ClaudeContentChunk[]) => AgentTimelineItem[],
): AgentTimelineItem[] {
  if (entry.type === "system" && entry.subtype === "compact_boundary") {
    const compactMetadata = readCompactionMetadata(entry as Record<string, unknown>);
    return [
      {
        type: "compaction",
        status: "completed",
        trigger: compactMetadata?.trigger === "manual" ? "manual" : "auto",
        preTokens: compactMetadata?.preTokens,
      },
    ];
  }

  const taskNotificationItem = mapTaskNotificationSystemRecordToToolCall(entry);
  if (taskNotificationItem) {
    return [taskNotificationItem];
  }

  if (entry.isCompactSummary) {
    return [];
  }
  if (entry.type === "user" && isSyntheticUserEntry(entry)) {
    return [];
  }

  const message = entry?.message;
  if (!message || !("content" in message)) {
    return [];
  }

  const content = message.content;
  if (
    (entry.type === "user" || entry.type === "assistant") &&
    isClaudeTranscriptNoiseContent(content)
  ) {
    return [];
  }
  const normalizedBlocks = normalizeHistoryBlocks(content);
  const contentValue = typeof content === "string" ? content : normalizedBlocks;
  const hasToolBlock = normalizedBlocks?.some((block) => hasToolLikeBlock(block)) ?? false;
  const userMessageId =
    entry.type === "user" && typeof entry.uuid === "string" && entry.uuid.length > 0
      ? entry.uuid
      : null;

  if (entry.type === "user") {
    const taskNotificationItem = mapTaskNotificationUserContentToToolCall({
      content,
      messageId: userMessageId,
    });
    if (taskNotificationItem) {
      return [taskNotificationItem];
    }
  }

  const timeline: AgentTimelineItem[] = [];

  if (entry.type === "user") {
    const text = extractUserMessageText(content);
    if (text) {
      timeline.push({
        type: "user_message",
        text,
        ...(userMessageId ? { messageId: userMessageId } : {}),
      });
    }
  }

  if (hasToolBlock && normalizedBlocks) {
    const mapped = mapBlocks(normalizedBlocks);
    if (entry.type === "user") {
      const toolItems = mapped.filter((item) => item.type === "tool_call");
      return timeline.length ? [...timeline, ...toolItems] : toolItems;
    }
    return mapped;
  }

  if (entry.type === "assistant" && contentValue) {
    return mapBlocks(contentValue);
  }

  return timeline;
}

function createAsyncMessageInput<T>(): AsyncMessageInput<T> {
  const queue: T[] = [];
  const resolvers: Array<(value: IteratorResult<T, void>) => void> = [];
  let closed = false;

  return {
    push(item: T) {
      if (closed) {
        return;
      }
      const resolve = resolvers.shift();
      if (resolve) {
        resolve({ value: item, done: false });
        return;
      }
      queue.push(item);
    },
    end() {
      closed = true;
      while (resolvers.length > 0) {
        const resolve = resolvers.shift();
        resolve?.({ value: undefined, done: true });
      }
    },
    iterable: {
      [Symbol.asyncIterator](): AsyncIterator<T, void> {
        return {
          next: (): Promise<IteratorResult<T, void>> => {
            if (queue.length > 0) {
              const value = queue.shift();
              if (value !== undefined) {
                return Promise.resolve({ value, done: false });
              }
            }
            if (closed) {
              return Promise.resolve({ value: undefined, done: true });
            }
            return new Promise<IteratorResult<T, void>>((resolve) => {
              resolvers.push(resolve);
            });
          },
        };
      },
    },
  };
}

type ClaudeSessionCandidate = {
  path: string;
  mtime: Date;
};

async function pathExists(target: string): Promise<boolean> {
  try {
    await fsPromises.access(target);
    return true;
  } catch {
    return false;
  }
}

async function collectRecentClaudeSessions(
  root: string,
  limit: number,
): Promise<ClaudeSessionCandidate[]> {
  let projectDirs: string[];
  try {
    projectDirs = await fsPromises.readdir(root);
  } catch {
    return [];
  }
  const candidates: ClaudeSessionCandidate[] = [];
  for (const dirName of projectDirs) {
    const projectPath = path.join(root, dirName);
    let stats: fs.Stats;
    try {
      stats = await fsPromises.stat(projectPath);
    } catch {
      continue;
    }
    if (!stats.isDirectory()) {
      continue;
    }
    let files: string[];
    try {
      files = await fsPromises.readdir(projectPath);
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.endsWith(".jsonl")) {
        continue;
      }
      const fullPath = path.join(projectPath, file);
      try {
        const fileStats = await fsPromises.stat(fullPath);
        candidates.push({ path: fullPath, mtime: fileStats.mtime });
      } catch {
        // ignore stat errors for individual files
      }
    }
  }
  return candidates.sort((a, b) => b.mtime.getTime() - a.mtime.getTime()).slice(0, limit);
}

async function parseClaudeSessionDescriptor(
  filePath: string,
  mtime: Date,
): Promise<PersistedAgentDescriptor | null> {
  let content: string;
  try {
    content = await fsPromises.readFile(filePath, "utf8");
  } catch {
    return null;
  }

  let sessionId: string | null = null;
  let cwd: string | null = null;
  let title: string | null = null;
  const timeline: AgentTimelineItem[] = [];

  for (const rawLine of content.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    let entry: any;
    try {
      entry = JSON.parse(line);
    } catch {
      continue;
    }
    if (entry?.isSidechain) {
      continue;
    }
    if (entry?.type === "user" && isSyntheticUserEntry(entry)) {
      continue;
    }
    if (!sessionId && typeof entry.sessionId === "string") {
      sessionId = entry.sessionId;
    }
    if (!cwd && typeof entry.cwd === "string") {
      cwd = entry.cwd;
    }
    if (entry.type === "user" && entry.message) {
      const text = extractClaudeUserText(entry.message);
      if (text) {
        if (!title) {
          title = text;
        }
        timeline.push({ type: "user_message", text });
      }
    } else if (entry.type === "assistant" && entry.message) {
      const text = extractClaudeUserText(entry.message);
      if (text) {
        timeline.push({ type: "assistant_message", text });
      }
    }
    if (sessionId && cwd && title) {
      break;
    }
  }

  if (!sessionId || !cwd) {
    return null;
  }

  const persistence: AgentPersistenceHandle = {
    provider: "claude",
    sessionId,
    nativeHandle: sessionId,
    metadata: {
      provider: "claude",
      cwd,
    },
  };

  return {
    provider: "claude",
    sessionId,
    cwd,
    title: (title ?? "").trim() || `Claude session ${sessionId.slice(0, 8)}`,
    lastActivityAt: mtime,
    persistence,
    timeline,
  };
}

function extractClaudeUserText(message: any): string | null {
  if (!message) {
    return null;
  }
  if (typeof message.content === "string") {
    const normalized = message.content.trim();
    return normalized && !isClaudeTranscriptNoiseText(normalized) ? normalized : null;
  }
  if (typeof message.text === "string") {
    const normalized = message.text.trim();
    return normalized && !isClaudeTranscriptNoiseText(normalized) ? normalized : null;
  }
  if (Array.isArray(message.content)) {
    for (const block of message.content) {
      if (block && typeof block.text === "string") {
        const normalized = block.text.trim();
        if (normalized && !isClaudeTranscriptNoiseText(normalized)) {
          return normalized;
        }
      }
    }
  }
  return null;
}

// Scans a partially-streamed JSON buffer for the file_path + content
// fields of a Write tool use, returning whatever portion of the content
// string is currently visible. Tolerates incomplete JSON — the content
// string may be unterminated, which we treat as "streaming in progress"
// and return everything we've accumulated so far.
//
// Returns null if the buffer doesn't yet contain enough to identify a
// content field.
function extractProgressiveWriteInput(
  buffer: string,
): { filePath: string | null; content: string } | null {
  const filePath = extractJsonStringField(buffer, "file_path");
  // Locate the start of the content field's string value. Accept any
  // whitespace between the key, colon, and opening quote.
  const keyMatch = /"content"\s*:\s*"/.exec(buffer);
  if (!keyMatch) {
    return null;
  }
  const start = keyMatch.index + keyMatch[0].length;
  const content = unescapePartialJsonString(buffer, start);
  return { filePath, content };
}

// Extracts a fully-closed JSON string field value by key from the buffer.
// Returns null if the field isn't complete in the buffer yet.
function extractJsonStringField(buffer: string, key: string): string | null {
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`"${escapedKey}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`);
  const match = pattern.exec(buffer);
  if (!match) {
    return null;
  }
  try {
    return JSON.parse(`"${match[1]}"`) as string;
  } catch {
    return match[1] ?? null;
  }
}

// Walk a partial JSON string starting at `start` (just past the opening
// quote), decoding escape sequences. Stops at the first unescaped closing
// quote OR the end of the buffer (whichever comes first). The second case
// is the streaming case — Claude hasn't finished writing the content yet.
function unescapePartialJsonString(buffer: string, start: number): string {
  let out = "";
  let i = start;
  const len = buffer.length;
  while (i < len) {
    const ch = buffer[i];
    if (ch === "\\") {
      const next = buffer[i + 1];
      if (next === undefined) {
        // Dangling backslash at end of buffer — next delta will carry
        // the escape target. Drop the partial escape rather than
        // emitting a raw backslash the client has to un-see later.
        break;
      }
      if (next === "n") out += "\n";
      else if (next === "r") out += "\r";
      else if (next === "t") out += "\t";
      else if (next === "b") out += "\b";
      else if (next === "f") out += "\f";
      else if (next === '"') out += '"';
      else if (next === "\\") out += "\\";
      else if (next === "/") out += "/";
      else if (next === "u") {
        if (i + 5 >= len) {
          // Incomplete \uXXXX at buffer end.
          break;
        }
        const hex = buffer.slice(i + 2, i + 6);
        const codepoint = Number.parseInt(hex, 16);
        if (!Number.isNaN(codepoint)) {
          out += String.fromCharCode(codepoint);
        }
        i += 6;
        continue;
      } else {
        out += next;
      }
      i += 2;
    } else if (ch === '"') {
      // Unescaped closing quote — content string complete.
      return out;
    } else {
      out += ch;
      i += 1;
    }
  }
  return out;
}
